import { useState, useEffect, useCallback, useRef } from "react";

// ─────────────────────────────────────────────────────────────
// BRAND
// ─────────────────────────────────────────────────────────────
const B = {
  name: "Vendifree",
  tagline: {
    en: "The marketplace for island territories.",
    pt: "O marketplace para territórios insulares.",
    fr: "La marketplace pour les territoires insulaires.",
  },
  c: {
    ink:       "#0D0D0D",
    deep:      "#111827",
    slate:     "#374151",
    muted:     "#6B7280",
    ghost:     "#9CA3AF",
    line:      "#E5E7EB",
    surface:   "#F9FAFB",
    white:     "#FFFFFF",
    gold:      "#B8892A",
    goldLight: "#F5EDD6",
    green:     "#166534",
    greenBg:   "#F0FDF4",
    red:       "#991B1B",
    redBg:     "#FEF2F2",
  },
  f: {
    display: "'Playfair Display', Georgia, serif",
    body:    "'DM Sans', system-ui, sans-serif",
    mono:    "'JetBrains Mono', Menlo, monospace",
  },
};

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
const CONFIG = {
  territories: [
    { code:"MT", name:"Malta",            currency:"EUR", language:"en", cities:["Valletta","Sliema","St Julian's","Mosta","Birkirkara","Gozo"] },
    { code:"CY", name:"Cyprus",           currency:"EUR", language:"en", cities:["Nicosia","Limassol","Larnaca","Paphos","Famagusta"] },
    { code:"IS", name:"Iceland",          currency:"ISK", language:"en", cities:["Reykjavik","Kópavogur","Hafnarfjörður","Akureyri"] },
    { code:"PF", name:"French Polynesia", currency:"XPF", language:"fr", cities:["Papeete","Faaa","Punaauia","Moorea","Bora Bora","Raiatea"] },
    { code:"NC", name:"New Caledonia",    currency:"XPF", language:"fr", cities:["Nouméa","Mont-Dore","Dumbéa","Païta"] },
    { code:"MQ", name:"Martinique",       currency:"EUR", language:"fr", cities:["Fort-de-France","Le Lamentin","Le Robert","Schoelcher"] },
    { code:"GP", name:"Guadeloupe",       currency:"EUR", language:"fr", cities:["Pointe-à-Pitre","Les Abymes","Le Gosier","Baie-Mahault"] },
    { code:"RE", name:"Réunion",          currency:"EUR", language:"fr", cities:["Saint-Denis","Saint-Paul","Saint-Pierre","Le Tampon"] },
    { code:"PT", name:"Portugal",         currency:"EUR", language:"pt", cities:["Lisboa","Porto","Braga","Coimbra","Faro","Aveiro"] },
    { code:"BR", name:"Brasil",           currency:"USD", language:"pt", cities:["São Paulo","Rio de Janeiro","Belo Horizonte","Curitiba","Salvador"] },
  ],
  categories: [
    { id:"electronics",  en:"Electronics",    pt:"Electrónicos",    fr:"Électronique"    },
    { id:"vehicles",     en:"Vehicles",       pt:"Veículos",        fr:"Véhicules"       },
    { id:"realestate",   en:"Real Estate",    pt:"Imobiliário",     fr:"Immobilier"      },
    { id:"fashion",      en:"Fashion",        pt:"Moda",            fr:"Mode"            },
    { id:"home",         en:"Home & Garden",  pt:"Casa e Jardim",   fr:"Maison & Jardin" },
    { id:"sports",       en:"Sports",         pt:"Desporto",        fr:"Sports"          },
    { id:"boats",        en:"Boats & Marine", pt:"Náutica",         fr:"Bateaux"         },
    { id:"crafts",       en:"Local Crafts",   pt:"Artesanato",      fr:"Artisanat"       },
    { id:"services",     en:"Services",       pt:"Serviços",        fr:"Services"        },
    { id:"pets",         en:"Pets",           pt:"Animais",         fr:"Animaux"         },
    { id:"food",         en:"Food & Produce", pt:"Produtos Locais", fr:"Alimentation"    },
    { id:"toys",         en:"Toys & Games",   pt:"Brinquedos",      fr:"Jouets"          },
  ],
  boostPlans: [
    { id:"b7",  name:{ en:"Featured — 7 days",  pt:"Destaque — 7 dias",  fr:"En vedette — 7 jours"  }, days:7,  price:{ EUR:3.99,  USD:4.49,  GBP:3.49,  ISK:599,  XPF:490,  AUD:6.49  } },
    { id:"b30", name:{ en:"Featured — 30 days", pt:"Destaque — 30 dias", fr:"En vedette — 30 jours" }, days:30, price:{ EUR:9.99,  USD:10.99, GBP:8.99,  ISK:1490, XPF:1190, AUD:15.99 }, popular:true },
    { id:"pro", name:{ en:"Pro monthly",         pt:"Pro mensal",          fr:"Pro mensuel"           }, days:30, price:{ EUR:19.99, USD:21.99, GBP:17.99, ISK:2990, XPF:2390, AUD:31.99 } },
  ],
  saasPlans: [
    { id:"starter",    name:"Starter",    monthly:29,  annual:290,  fee:3, limits:"1 territory · 500 listings",    features:{ en:["1 territory","500 listings","Custom domain","Analytics","Email support"], pt:["1 território","500 anúncios","Domínio próprio","Analytics","Suporte email"], fr:["1 territoire","500 annonces","Domaine personnalisé","Analytics","Support email"] } },
    { id:"growth",     name:"Growth",     monthly:79,  annual:790,  fee:2, limits:"5 territories · 5 000 listings", popular:true, features:{ en:["5 territories","5 000 listings","Custom domain","Advanced analytics","Ambassador programme","Priority support"], pt:["5 territórios","5 000 anúncios","Domínio próprio","Analytics avançado","Programa embaixadores","Suporte prioritário"], fr:["5 territoires","5 000 annonces","Domaine personnalisé","Analytiques avancées","Programme ambassadeurs","Support prioritaire"] } },
    { id:"enterprise", name:"Enterprise", monthly:199, annual:1990, fee:1, limits:"Unlimited",                      features:{ en:["Unlimited territories","Unlimited listings","White label","Custom features","Dedicated support","SLA"], pt:["Territórios ilimitados","Anúncios ilimitados","White label","Funcionalidades custom","Suporte dedicado","SLA"], fr:["Territoires illimités","Annonces illimitées","Marque blanche","Fonctionnalités custom","Support dédié","SLA"] } },
  ],
  rates:    { EUR:1, USD:1.09, GBP:0.86, ISK:150, XPF:119.33, AUD:1.65 },
  decimals: { EUR:2, USD:2,    GBP:2,    ISK:0,   XPF:0,       AUD:2    },
  ambassadorFee: 0.25,
  maxFreeAds: 5,
};

// ─────────────────────────────────────────────────────────────
// TRANSLATIONS
// ─────────────────────────────────────────────────────────────
const T = {
  en: {
    search:"Search listings",post:"Post listing",signIn:"Sign in",signOut:"Sign out",
    register:"Create account",myListings:"My listings",saved:"Saved",
    allCats:"All categories",newest:"Most recent",priceLow:"Price: low to high",priceHigh:"Price: high to low",
    loadMore:"Load more",free:"Free",urgent:"Urgent",featured:"Featured",
    negotiable:"Negotiable",pro:"Pro",showPhone:"Reveal phone number",sendMsg:"Send message",
    saveAd:"Save listing",share:"Share",report:"Report",
    views:"views",condition:"Condition",new:"New",likeNew:"Like new",good:"Good condition",used:"Used",
    back:"Back",close:"Close",next:"Next",step:"Step",of:"of",
    title:"Title",description:"Description",price:"Price",category:"Category",
    city:"City",territory:"Territory",phone:"Phone",photos:"Photos",
    urgentCheck:"Mark as urgent",negCheck:"Open to offers",freeCheck:"Free listing",
    publish:"Publish listing",posted:"Your listing is live.",
    email:"Email",password:"Password",name:"Full name",confirmPwd:"Confirm password",
    ambassador:"Ambassador programme",launchPlatform:"For businesses",seoMarketing:"SEO & Marketing",
    ambHero:"Earn 25% of all revenue in your territory.",
    applyNow:"Apply now",whyAmb:"Why do you want to be an ambassador?",
    socialLink:"Social media profile",reach:"Estimated audience",
    u500:"Under 500","500_2k":"500 – 2 000","2k_10k":"2 000 – 10 000",o10k:"Over 10 000",
    agreeTerms:"I agree to the Ambassador terms and conditions",
    submitApp:"Submit application",appReceived:"Application received. We will be in touch within 24 hours.",
    ambDash:"Ambassador dashboard",refLink:"My referral link",
    copyLink:"Copy link",templates:"Message templates",copy:"Copy",
    payHistory:"Payment history",paid:"Paid",pending:"Pending",
    adminPanel:"Administration",pendingApps:"Pending applications",
    approve:"Approve",reject:"Decline",activeAmb:"Active ambassadors",markPaid:"Mark as paid",
    boost:"Promote listing",selectPlan:"Select a plan",proceedPay:"Proceed to payment",
    cardName:"Name on card",cardNum:"Card number",expiry:"MM / YY",cvv:"CVV",
    choosePlan:"Choose your plan",monthly:"Monthly",annual:"Annual",save20:"Save 20%",
    startTrial:"Start free trial",seoTitle:"SEO & Marketing",
    adminListings:"Listings",adminOverview:"Overview",adminAmbassadors:"Ambassadors",
  },
  pt: {
    search:"Pesquisar anúncios",post:"Publicar anúncio",signIn:"Entrar",signOut:"Sair",
    register:"Criar conta",myListings:"Os meus anúncios",saved:"Guardados",
    allCats:"Todas as categorias",newest:"Mais recentes",priceLow:"Preço crescente",priceHigh:"Preço decrescente",
    loadMore:"Carregar mais",free:"Grátis",urgent:"Urgente",featured:"Destaque",
    negotiable:"Negociável",pro:"Pro",showPhone:"Ver número de telefone",sendMsg:"Enviar mensagem",
    saveAd:"Guardar",share:"Partilhar",report:"Reportar",
    views:"visualizações",condition:"Estado",new:"Novo",likeNew:"Como novo",good:"Bom estado",used:"Usado",
    back:"Voltar",close:"Fechar",next:"Próximo",step:"Passo",of:"de",
    title:"Título",description:"Descrição",price:"Preço",category:"Categoria",
    city:"Cidade",territory:"Território",phone:"Telefone",photos:"Fotografias",
    urgentCheck:"Marcar como urgente",negCheck:"Aceito propostas",freeCheck:"Anúncio gratuito",
    publish:"Publicar anúncio",posted:"O teu anúncio está no ar.",
    email:"Email",password:"Palavra-passe",name:"Nome completo",confirmPwd:"Confirmar palavra-passe",
    ambassador:"Programa de embaixadores",launchPlatform:"Para empresas",seoMarketing:"SEO & Marketing",
    ambHero:"Ganha 25% de toda a receita no teu território.",
    applyNow:"Candidatar-me",whyAmb:"Porque queres ser embaixador?",
    socialLink:"Perfil nas redes sociais",reach:"Dimensão estimada da audiência",
    u500:"Menos de 500","500_2k":"500 – 2 000","2k_10k":"2 000 – 10 000",o10k:"Mais de 10 000",
    agreeTerms:"Concordo com os termos e condições do programa de embaixadores",
    submitApp:"Enviar candidatura",appReceived:"Candidatura recebida. Entraremos em contacto em 24 horas.",
    ambDash:"Painel do embaixador",refLink:"O meu link de referência",
    copyLink:"Copiar link",templates:"Templates de mensagem",copy:"Copiar",
    payHistory:"Histórico de pagamentos",paid:"Pago",pending:"Pendente",
    adminPanel:"Administração",pendingApps:"Candidaturas pendentes",
    approve:"Aprovar",reject:"Recusar",activeAmb:"Embaixadores activos",markPaid:"Marcar como pago",
    boost:"Promover anúncio",selectPlan:"Escolher plano",proceedPay:"Avançar para pagamento",
    cardName:"Nome no cartão",cardNum:"Número do cartão",expiry:"MM / AA",cvv:"CVV",
    choosePlan:"Escolhe o plano",monthly:"Mensal",annual:"Anual",save20:"Poupa 20%",
    startTrial:"Começar gratuitamente",seoTitle:"SEO & Marketing",
    adminListings:"Anúncios",adminOverview:"Visão geral",adminAmbassadors:"Embaixadores",
  },
  fr: {
    search:"Rechercher des annonces",post:"Publier une annonce",signIn:"Connexion",signOut:"Déconnexion",
    register:"Créer un compte",myListings:"Mes annonces",saved:"Sauvegardés",
    allCats:"Toutes les catégories",newest:"Plus récents",priceLow:"Prix croissant",priceHigh:"Prix décroissant",
    loadMore:"Charger plus",free:"Gratuit",urgent:"Urgent",featured:"En vedette",
    negotiable:"Négociable",pro:"Pro",showPhone:"Afficher le téléphone",sendMsg:"Envoyer un message",
    saveAd:"Sauvegarder",share:"Partager",report:"Signaler",
    views:"vues",condition:"État",new:"Neuf",likeNew:"Comme neuf",good:"Bon état",used:"Utilisé",
    back:"Retour",close:"Fermer",next:"Suivant",step:"Étape",of:"sur",
    title:"Titre",description:"Description",price:"Prix",category:"Catégorie",
    city:"Ville",territory:"Territoire",phone:"Téléphone",photos:"Photos",
    urgentCheck:"Marquer comme urgent",negCheck:"Ouvert aux offres",freeCheck:"Annonce gratuite",
    publish:"Publier l'annonce",posted:"Votre annonce est en ligne.",
    email:"Email",password:"Mot de passe",name:"Nom complet",confirmPwd:"Confirmer le mot de passe",
    ambassador:"Programme ambassadeur",launchPlatform:"Pour les entreprises",seoMarketing:"SEO & Marketing",
    ambHero:"Gagnez 25% de tous les revenus dans votre territoire.",
    applyNow:"Postuler",whyAmb:"Pourquoi voulez-vous être ambassadeur?",
    socialLink:"Profil réseaux sociaux",reach:"Taille estimée de l'audience",
    u500:"Moins de 500","500_2k":"500 – 2 000","2k_10k":"2 000 – 10 000",o10k:"Plus de 10 000",
    agreeTerms:"J'accepte les conditions du programme ambassadeur",
    submitApp:"Soumettre la candidature",appReceived:"Candidature reçue. Nous vous contacterons sous 24 heures.",
    ambDash:"Tableau de bord ambassadeur",refLink:"Mon lien de parrainage",
    copyLink:"Copier le lien",templates:"Modèles de messages",copy:"Copier",
    payHistory:"Historique des paiements",paid:"Payé",pending:"En attente",
    adminPanel:"Administration",pendingApps:"Candidatures en attente",
    approve:"Approuver",reject:"Refuser",activeAmb:"Ambassadeurs actifs",markPaid:"Marquer comme payé",
    boost:"Promouvoir l'annonce",selectPlan:"Choisir un plan",proceedPay:"Procéder au paiement",
    cardName:"Nom sur la carte",cardNum:"Numéro de carte",expiry:"MM / AA",cvv:"CVV",
    choosePlan:"Choisir un plan",monthly:"Mensuel",annual:"Annuel",save20:"Économisez 20%",
    startTrial:"Commencer gratuitement",seoTitle:"SEO & Marketing",
    adminListings:"Annonces",adminOverview:"Vue d'ensemble",adminAmbassadors:"Ambassadeurs",
  },
};

// ─────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────
const MOCK_ADS = [
  { id:"1",  title:"iPhone 15 Pro Max 256 GB",                  description:"Excellent condition. Original box. Purchased 3 months ago, barely used.",                           price:850,   currency:"EUR", category:"electronics", territory:"MT", city:"Sliema",        condition:"likeNew", featured:true,  urgent:false, free:false, negotiable:true,  pro:false, views:234, seller:"Jonathan M.", phone:"+356 9912 3456", date:"2024-01-15", img:"https://picsum.photos/seed/iphone15pro/600/400" },
  { id:"2",  title:"Toyota Yaris 2019 — Full Service History",   description:"35 000 km. New tyres. Air conditioning. Full service history. One owner from new.",                price:12500, currency:"EUR", category:"vehicles",    territory:"MT", city:"Valletta",      condition:"good",    featured:true,  urgent:false, free:false, negotiable:true,  pro:true,  views:567, seller:"Maria B.",     phone:"+356 7712 3456", date:"2024-01-14", img:"https://picsum.photos/seed/toyota19/600/400" },
  { id:"3",  title:"Overwater Bungalow — Bora Bora",             description:"Stunning overwater bungalow with direct lagoon access. Ideal for long stays. Weekly rental.",       price:450,   currency:"XPF", category:"realestate",  territory:"PF", city:"Bora Bora",     condition:"new",     featured:true,  urgent:false, free:false, negotiable:false, pro:true,  views:1203,seller:"Teiva H.",     phone:"+689 87 12 34 56",date:"2024-01-13", img:"https://picsum.photos/seed/borabora22/600/400" },
  { id:"4",  title:"MacBook Pro M3 14-inch",                     description:"Purchased 2 months ago. Original charger and sleeve included. Like new condition.",                 price:1800,  currency:"EUR", category:"electronics", territory:"CY", city:"Limassol",      condition:"likeNew", featured:false, urgent:true,  free:false, negotiable:false, pro:false, views:89,  seller:"Andreas K.",   phone:"+357 9912 3456", date:"2024-01-12", img:"https://picsum.photos/seed/macbookm3/600/400" },
  { id:"5",  title:"Handknitted Icelandic Lopapeysa — Size L",   description:"Traditional wool sweater, handmade locally. Perfect condition, never worn outdoors.",               price:15000, currency:"ISK", category:"crafts",      territory:"IS", city:"Reykjavik",     condition:"good",    featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:45,  seller:"Sigrid E.",    phone:"+354 555 1234",  date:"2024-01-11", img:"https://picsum.photos/seed/sweater88/600/400" },
  { id:"6",  title:"Appartement T3 — Vue mer Fort-de-France",    description:"85 m² avec terrasse et vue panoramique sur la baie. Entièrement rénové. Proche de toutes commodités.",price:950,   currency:"EUR", category:"realestate",  territory:"MQ", city:"Fort-de-France", condition:"good",    featured:true,  urgent:false, free:false, negotiable:true,  pro:true,  views:432, seller:"Marie-Claire D.",phone:"+596 696 12 34 56",date:"2024-01-10",img:"https://picsum.photos/seed/apart77/600/400" },
  { id:"7",  title:"Sea Kayak Double — Ocean Touring",           description:"Paddles included. Lightly used. Excellent condition. Ideal for lagoon exploration.",                price:35000, currency:"XPF", category:"boats",       territory:"PF", city:"Papeete",       condition:"good",    featured:false, urgent:true,  free:false, negotiable:true,  pro:false, views:178, seller:"Teva M.",      phone:"+689 87 45 67 89",date:"2024-01-09",img:"https://picsum.photos/seed/kayak44/600/400" },
  { id:"8",  title:"Golden Labrador Puppies — 8 weeks",          description:"Vaccinated and microchipped. Looking for loving permanent homes. Ready to leave.",                   price:0,     currency:"EUR", category:"pets",        territory:"MT", city:"Mosta",         condition:"new",     featured:false, urgent:false, free:true,  negotiable:false, pro:false, views:892, seller:"Claire G.",    phone:"+356 7734 5678", date:"2024-01-08", img:"https://picsum.photos/seed/labrador9/600/400" },
  { id:"9",  title:"Apartamento T2 — Porto Histórico",           description:"Apartamento renovado no centro histórico do Porto. 65 m², totalmente equipado.",                   price:1200,  currency:"EUR", category:"realestate",  territory:"PT", city:"Porto",         condition:"good",    featured:true,  urgent:false, free:false, negotiable:false, pro:true,  views:756, seller:"Ana S.",       phone:"+351 912 345 678",date:"2024-01-07",img:"https://picsum.photos/seed/porto33/600/400" },
  { id:"10", title:"Vélo Électrique Kalkhoff — 500 Wh",          description:"Autonomie 100 km. Excellent état. Vendu avec antivol et sacoches de voyage.",                      price:1200,  currency:"EUR", category:"sports",      territory:"RE", city:"Saint-Denis",    condition:"good",    featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:123, seller:"Patrick L.",   phone:"+262 692 12 34 56",date:"2024-01-06",img:"https://picsum.photos/seed/velo55/600/400" },
  { id:"11", title:"PlayStation 5 — Disc Edition Bundle",        description:"PS5 with Spider-Man 2, FIFA 24 and God of War Ragnarok. All in excellent condition.",              price:520,   currency:"EUR", category:"electronics", territory:"CY", city:"Nicosia",       condition:"good",    featured:false, urgent:true,  free:false, negotiable:true,  pro:false, views:345, seller:"Nikos P.",     phone:"+357 9956 7890", date:"2024-01-04", img:"https://picsum.photos/seed/ps566/600/400" },
  { id:"12", title:"Northern Lights Photography Tour",           description:"Professional guided astrophotography tour. Maximum 8 guests. All equipment provided.",              price:45000, currency:"ISK", category:"services",    territory:"IS", city:"Reykjavik",     condition:"new",     featured:true,  urgent:false, free:false, negotiable:false, pro:true,  views:512, seller:"Bjorn A.",     phone:"+354 777 1234",  date:"2023-12-31", img:"https://picsum.photos/seed/aurora11/600/400" },
  { id:"13", title:"iPhone 13 128 GB — Blue",                    description:"iPhone 13 em excelente estado. Bateria a 95%. Com capa e película de protecção originais.",       price:550,   currency:"EUR", category:"electronics", territory:"PT", city:"Lisboa",        condition:"good",    featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:234, seller:"Pedro M.",     phone:"+351 963 456 789",date:"2024-01-01",img:"https://picsum.photos/seed/iphone13b/600/400" },
  { id:"14", title:"Artisan Handwoven Tapestries",               description:"Hand-woven by certified local artisans. Each piece unique. Certificate of authenticity.",          price:80,    currency:"EUR", category:"crafts",      territory:"PT", city:"Lisboa",        condition:"new",     featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:67,  seller:"Rosa F.",      phone:"+351 934 567 890",date:"2024-01-05",img:"https://picsum.photos/seed/tapes22/600/400" },
  { id:"15", title:"Shortboard 6'2 — Performance Surf",          description:"Ideal for intermediate to advanced surfers. Minor repairs done professionally. Fast board.",      price:28000, currency:"XPF", category:"sports",      territory:"PF", city:"Moorea",        condition:"good",    featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:89,  seller:"Keanu T.",     phone:"+689 87 23 45 67",date:"2024-01-03",img:"https://picsum.photos/seed/surf77/600/400" },
  { id:"16", title:"Planche de Surf Vintage — Années 80",        description:"Pièce de collection rare. Excellent état général. Idéale comme décoration ou compétition vintage.",price:45000, currency:"XPF", category:"sports",      territory:"NC", city:"Nouméa",        condition:"used",    featured:false, urgent:false, free:false, negotiable:true,  pro:false, views:34,  seller:"Michel B.",    phone:"+687 12 34 56",  date:"2024-01-02", img:"https://picsum.photos/seed/vintsrf/600/400" },
];

const MOCK_AMB_APPS = [
  { id:"a1", name:"Lucas Ferreira",  email:"lucas@email.com", territory:"PT", city:"Porto",          phone:"+351 912 345 678", why:"I am very active in local buy-and-sell groups and have been promoting local commerce for over 3 years. I have strong connections with buyers and sellers across the Porto region.", social:"https://facebook.com/lucasf", reach:"2k_10k", status:"pending", date:"2024-01-10" },
  { id:"a2", name:"Emma Martin",     email:"emma@email.fr",   territory:"MQ", city:"Fort-de-France", phone:"+596 696 12 34 56", why:"Je suis très active sur les réseaux locaux martiniquais et connais bien la communauté. J'ai déjà aidé à lancer deux initiatives locales avec succès.", social:"https://instagram.com/emmamartin", reach:"500_2k", status:"pending", date:"2024-01-09" },
];

const MOCK_ACTIVE_AMB = [
  { id:"m1", name:"Carlos Mifsud",  territory:"MT", users:145, revenue:420, commission:105 },
  { id:"m2", name:"Sophie Laurent", territory:"PF", users:89,  revenue:280, commission:70  },
  { id:"m3", name:"James Kowalski", territory:"IS", users:201, revenue:530, commission:132 },
];

// ─────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────
const fmt = (price, cur) => {
  if (price === 0) return "Free";
  const dec = CONFIG.decimals[cur] ?? 2;
  const sym = { EUR:"€", USD:"$", GBP:"£", ISK:"kr", XPF:"XPF", AUD:"A$" };
  const val = dec === 0 ? Math.round(price) : price.toFixed(2);
  return cur === "XPF" || cur === "ISK" ? `${val} ${sym[cur]}` : `${sym[cur]}${val}`;
};

const ago = (d, lang) => {
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (diff === 0) return lang==="fr"?"Aujourd'hui":lang==="pt"?"Hoje":"Today";
  if (diff === 1) return lang==="fr"?"Hier":lang==="pt"?"Ontem":"Yesterday";
  return lang==="fr"?`Il y a ${diff} jours`:lang==="pt"?`Há ${diff} dias`:`${diff}d ago`;
};

const luhn = n => { const a=n.replace(/\D/g,"").split("").reverse().map(Number); return a.reduce((s,d,i)=>{if(i%2)d=d*2>9?d*2-9:d*2;return s+d;},0)%10===0; };
const cardBrand = n => /^4/.test(n.replace(/\D/g,""))?"Visa":/^5[1-5]/.test(n.replace(/\D/g,""))?"Mastercard":"";
const fmtCard = v => v.replace(/\D/g,"").slice(0,16).replace(/(\d{4})/g,"$1 ").trim();
const fmtExp  = v => { const d=v.replace(/\D/g,"").slice(0,4); return d.length>=2?d.slice(0,2)+"/"+d.slice(2):d; };

// Analytics
const track = (e,d) => console.log("[Vendifree]", e, d); // REPLACE WITH gtag / Plausible

// SEO
const setMeta = (title, desc) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m=document.createElement("meta"); m.name="description"; document.head.appendChild(m); }
  m.content = desc;
};

// ─────────────────────────────────────────────────────────────
// DESIGN SYSTEM
// ─────────────────────────────────────────────────────────────
const s = {
  // Cards
  card: {
    background: B.c.white,
    border: `1px solid ${B.c.line}`,
    borderRadius: 8,
    padding: 20,
  },
  // Inputs
  input: (err=false) => ({
    width: "100%",
    padding: "10px 14px",
    border: `1.5px solid ${err ? B.c.red : B.c.line}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: B.f.body,
    color: B.c.ink,
    outline: "none",
    boxSizing: "border-box",
    background: B.c.white,
    transition: "border-color 0.15s",
  }),
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: B.c.muted,
    marginBottom: 6,
    letterSpacing: 1,
    textTransform: "uppercase",
    fontFamily: B.f.body,
  },
  // Buttons
  btn: {
    primary: { background: B.c.ink,  color: B.c.white, border: "none" },
    gold:    { background: B.c.gold, color: B.c.white, border: "none" },
    outline: { background: "transparent", color: B.c.ink, border: `1.5px solid ${B.c.ink}` },
    ghost:   { background: "transparent", color: B.c.muted, border: `1.5px solid ${B.c.line}` },
    danger:  { background: B.c.red,   color: B.c.white, border: "none" },
    success: { background: B.c.green, color: B.c.white, border: "none" },
    white:   { background: B.c.white, color: B.c.ink,   border: "none" },
  },
  // Badge
  badge: (color, bg, uppercase=true) => ({
    background: bg,
    color,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: uppercase ? 1.2 : 0,
    textTransform: uppercase ? "uppercase" : "none",
    padding: "3px 8px",
    borderRadius: 3,
    fontFamily: B.f.body,
    whiteSpace: "nowrap",
    display: "inline-block",
  }),
};

// ─────────────────────────────────────────────────────────────
// BASE COMPONENTS
// ─────────────────────────────────────────────────────────────
const Spinner = () => (
  <span style={{ width:14, height:14, border:`2px solid rgba(255,255,255,0.3)`, borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"vf-spin 0.6s linear infinite" }} />
);

const Btn = ({ children, onClick, variant="primary", disabled, loading, full, sm, type="button", style:extra={} }) => (
  <button type={type} onClick={onClick} disabled={disabled||loading}
    style={{ ...s.btn[variant], padding: sm ? "7px 14px" : "10px 22px", borderRadius:6, fontSize: sm ? 12 : 14, fontWeight:700, fontFamily:B.f.body, cursor:disabled||loading?"not-allowed":"pointer", opacity:disabled||loading?0.45:1, width:full?"100%":"auto", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, transition:"opacity 0.15s", letterSpacing:0.3, ...extra }}>
    {loading&&<Spinner/>}{children}
  </button>
);

const Field = ({ label, value, onChange, type="text", placeholder, required, maxLen, error, rows, readOnly }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label style={s.label}>{label}{required&&<span style={{color:B.c.red}}> *</span>}</label>}
    {rows
      ? <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows} maxLength={maxLen} placeholder={placeholder} readOnly={readOnly}
          style={{ ...s.input(!!error), resize:"vertical" }}
          onFocus={e=>!readOnly&&(e.target.style.borderColor=B.c.gold)}
          onBlur={e=>e.target.style.borderColor=error?B.c.red:B.c.line} />
      : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} maxLength={maxLen} readOnly={readOnly}
          style={{ ...s.input(!!error), cursor:readOnly?"default":"text" }}
          onFocus={e=>!readOnly&&(e.target.style.borderColor=B.c.gold)}
          onBlur={e=>e.target.style.borderColor=error?B.c.red:B.c.line} />
    }
    {error && <p style={{fontSize:12,color:B.c.red,margin:"4px 0 0",fontFamily:B.f.body}}>{error}</p>}
    {maxLen && <p style={{fontSize:11,color:B.c.ghost,margin:"4px 0 0",textAlign:"right",fontFamily:B.f.body}}>{value.length}/{maxLen}</p>}
  </div>
);

const Sel = ({ label, value, onChange, options, required }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label style={s.label}>{label}{required&&<span style={{color:B.c.red}}> *</span>}</label>}
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ ...s.input(), appearance:"none", backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%236B7280' d='M5 7L1 2h8z'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"right 12px center" }}>
      {options.map(([v,l])=><option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const Modal = ({ onClose, children, wide }) => (
  <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(4px)" }}
    onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:B.c.white, borderRadius:10, width:"100%", maxWidth:wide?840:480, maxHeight:"92vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,0.18)" }}>
      {children}
    </div>
  </div>
);

const ModalHead = ({ title, onClose }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 28px", borderBottom:`1px solid ${B.c.line}`, position:"sticky", top:0, background:B.c.white, zIndex:1 }}>
    <h2 style={{ margin:0, fontSize:17, fontWeight:700, fontFamily:B.f.display, color:B.c.ink }}>{title}</h2>
    <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:B.c.ghost, lineHeight:1, padding:4 }}>×</button>
  </div>
);

const Toast = ({ items }) => (
  <div style={{ position:"fixed", bottom:24, left:24, zIndex:9999, display:"flex", flexDirection:"column", gap:8 }}>
    {items.map(t=>(
      <div key={t.id} style={{ background:t.type==="error"?B.c.red:t.type==="warning"?"#92400E":B.c.green, color:B.c.white, padding:"11px 18px", borderRadius:6, fontSize:13, fontWeight:500, fontFamily:B.f.body, boxShadow:"0 4px 20px rgba(0,0,0,0.15)", maxWidth:340, animation:"vf-in 0.25s ease" }}>
        {t.msg}
      </div>
    ))}
  </div>
);

const HR = () => <div style={{ height:1, background:B.c.line, margin:"20px 0" }} />;

// ─────────────────────────────────────────────────────────────
// CHECKOUT
// ─────────────────────────────────────────────────────────────
const Checkout = ({ plan, currency, onClose, onSuccess, lang }) => {
  const [name,setName]=useState(""); const [num,setNum]=useState(""); const [exp,setExp]=useState(""); const [cvv,setCvv]=useState(""); const [ok,setOk]=useState(false);
  const [loading,setLoading]=useState(false); const [done,setDone]=useState(false); const [err,setErr]=useState("");
  const t = T[lang]||T.en;
  const price = plan.price[currency];
  const dec = CONFIG.decimals[currency]??2;
  const brand = cardBrand(num);
  const valid = num.replace(/\D/g,"").length===16 && luhn(num);

  const pay = async () => {
    if (!ok) return;
    setLoading(true); setErr("");
    await new Promise(r=>setTimeout(r,1800));
    setLoading(false); setDone(true);
    setTimeout(()=>{onSuccess();onClose();},2200);
    /* REPLACE WITH STRIPE:
       const stripe = await loadStripe(import.meta.env.VITE_STRIPE_KEY);
       const session = await fetch('/api/checkout', { method:'POST',
         body: JSON.stringify({ planId: plan.id, currency }) }).then(r=>r.json());
       stripe.redirectToCheckout({ sessionId: session.id });
       NOTE: ISK + XPF = integer amounts, no multiplication by 100 */
  };

  if (done) return (
    <Modal onClose={onClose}>
      <div style={{ padding:"64px 32px", textAlign:"center" }}>
        <div style={{ width:52, height:52, borderRadius:"50%", background:B.c.greenBg, border:`2px solid ${B.c.green}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:22, color:B.c.green }}>✓</div>
        <h2 style={{ fontFamily:B.f.display, color:B.c.green, margin:"0 0 8px" }}>Payment confirmed</h2>
        <p style={{ color:B.c.muted, fontSize:14 }}>{plan.name[lang]||plan.name.en} — {dec===0?Math.round(price):price} {currency}</p>
      </div>
    </Modal>
  );

  return (
    <Modal onClose={onClose}>
      <ModalHead title={t.proceedPay} onClose={onClose}/>
      <div style={{ padding:28 }}>
        <div style={{ background:B.c.surface, borderRadius:6, padding:16, marginBottom:24 }}>
          <p style={{ margin:"0 0 2px", fontWeight:600, fontSize:13, color:B.c.ink }}>{plan.name[lang]||plan.name.en}</p>
          <p style={{ margin:0, fontSize:24, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>{dec===0?Math.round(price):price} {currency}</p>
        </div>
        <Field label={t.cardName} value={name} onChange={setName} required/>
        <div style={{ marginBottom:16 }}>
          <label style={s.label}>{t.cardNum} *</label>
          <div style={{ position:"relative" }}>
            <input value={num} onChange={e=>setNum(fmtCard(e.target.value))} placeholder="4242 4242 4242 4242" maxLength={19}
              style={{ ...s.input(num.replace(/\D/g,"").length===16&&!valid), paddingRight:70 }}
              onFocus={e=>e.target.style.borderColor=B.c.gold}
              onBlur={e=>e.target.style.borderColor=num.replace(/\D/g,"").length===16&&!valid?B.c.red:B.c.line}/>
            {brand&&<span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:10, color:B.c.muted, fontWeight:800, letterSpacing:0.5, fontFamily:B.f.mono }}>{brand}</span>}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Field label={t.expiry} value={exp} onChange={v=>setExp(fmtExp(v))} placeholder="12/27" maxLength={5} required/>
          <Field label={t.cvv}    value={cvv} onChange={setCvv} type="password" placeholder="123" maxLength={4} required/>
        </div>
        {err&&<div style={{ background:B.c.redBg, border:`1px solid ${B.c.red}`, borderRadius:6, padding:12, color:B.c.red, fontSize:13, marginBottom:16 }}>{err}</div>}
        <label style={{ display:"flex", gap:10, cursor:"pointer", marginBottom:20, fontSize:13, color:B.c.muted, fontFamily:B.f.body }}>
          <input type="checkbox" checked={ok} onChange={e=>setOk(e.target.checked)} style={{ marginTop:2 }}/>
          I agree to the Terms and Conditions
        </label>
        <Btn onClick={pay} loading={loading} disabled={!name||!valid||exp.length<5||cvv.length<3||!ok} full>
          Confirm payment
        </Btn>
        <p style={{ textAlign:"center", fontSize:11, color:B.c.ghost, marginTop:12, fontFamily:B.f.mono }}>Secured by Stripe</p>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// BOOST MODAL
// ─────────────────────────────────────────────────────────────
const BoostModal = ({ ad, territory, lang, onClose, addToast }) => {
  const [sel,setSel]=useState(null); const [paying,setPaying]=useState(false);
  const t = T[lang]||T.en;
  if (paying&&sel) return <Checkout plan={sel} currency={territory.currency} lang={lang} onClose={()=>setPaying(false)} onSuccess={()=>{addToast(`${sel.name[lang]||sel.name.en} activated`,"success");onClose();}}/>;
  return (
    <Modal onClose={onClose} wide>
      <ModalHead title={t.boost} onClose={onClose}/>
      <div style={{ padding:28 }}>
        <p style={{ color:B.c.muted, fontSize:14, marginBottom:24 }}>{t.selectPlan}</p>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:28 }}>
          {CONFIG.boostPlans.map(p=>(
            <div key={p.id} onClick={()=>setSel(p)}
              style={{ border:`2px solid ${sel?.id===p.id?B.c.gold:B.c.line}`, borderRadius:8, padding:24, cursor:"pointer", background:sel?.id===p.id?B.c.goldLight:B.c.white, transition:"all 0.15s", position:"relative" }}>
              {p.popular&&<span style={{ ...s.badge(B.c.white,B.c.ink), position:"absolute", top:-10, left:"50%", transform:"translateX(-50%)" }}>Most popular</span>}
              <p style={{ margin:"0 0 8px", fontWeight:700, fontSize:13, color:B.c.ink }}>{p.name[lang]||p.name.en}</p>
              <p style={{ margin:"0 0 4px", fontSize:22, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>
                {CONFIG.decimals[territory.currency]===0?Math.round(p.price[territory.currency]):p.price[territory.currency]} {territory.currency}
              </p>
              <p style={{ margin:0, fontSize:12, color:B.c.muted }}>{p.days} days</p>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
          <Btn onClick={onClose} variant="ghost">{t.close}</Btn>
          <Btn onClick={()=>sel&&setPaying(true)} disabled={!sel}>{t.proceedPay}</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// POST AD
// ─────────────────────────────────────────────────────────────
const PostAd = ({ lang, territory, user, onClose, onPost, addToast }) => {
  const [step,setStep]=useState(1);
  const [title,setTitle]=useState(""); const [cat,setCat]=useState(""); const [city,setCity]=useState("");
  const [desc,setDesc]=useState(""); const [price,setPrice]=useState(""); const [cond,setCond]=useState("good");
  const [urgent,setUrgent]=useState(false); const [neg,setNeg]=useState(false); const [isFree,setIsFree]=useState(false);
  const [phone,setPhone]=useState(""); const [photos,setPhotos]=useState([]);
  const [loading,setLoading]=useState(false); const [done,setDone]=useState(false);
  const t = T[lang]||T.en;

  const handlePhoto = e => Array.from(e.target.files).slice(0,3).forEach(f=>{ const r=new FileReader(); r.onload=ev=>setPhotos(p=>[...p,ev.target.result].slice(0,3)); r.readAsDataURL(f); });

  const submit = async () => {
    setLoading(true);
    await new Promise(r=>setTimeout(r,1000));
    onPost({ id:Date.now().toString(), title, description:desc, price:isFree?0:Number(price), currency:territory.currency, category:cat, territory:territory.code, city, condition:cond, urgent, negotiable:neg, free:isFree, featured:false, pro:false, views:0, seller:user?.name||"User", phone, date:new Date().toISOString().split("T")[0], img:photos[0]||`https://picsum.photos/seed/${Date.now()}/600/400` });
    setLoading(false); setDone(true);
    track("ad_posted",{ territory:territory.code, category:cat });
  };

  if (done) return (
    <Modal onClose={onClose}>
      <div style={{ padding:"64px 32px", textAlign:"center" }}>
        <div style={{ width:52,height:52,borderRadius:"50%",background:B.c.greenBg,border:`2px solid ${B.c.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:22,color:B.c.green }}>✓</div>
        <h2 style={{ fontFamily:B.f.display, color:B.c.green, margin:"0 0 8px" }}>{t.posted}</h2>
      </div>
    </Modal>
  );

  return (
    <Modal onClose={onClose} wide>
      <ModalHead title={`${t.step} ${step} ${t.of} 3 — ${t.post}`} onClose={onClose}/>
      <div style={{ padding:28 }}>
        <div style={{ display:"flex", gap:3, marginBottom:28 }}>
          {[1,2,3].map(n=><div key={n} style={{ flex:1, height:2, borderRadius:1, background:n<=step?B.c.gold:B.c.line, transition:"background 0.3s" }}/>)}
        </div>

        {step===1&&<>
          <Field label={t.title} value={title} onChange={setTitle} required maxLen={100} placeholder="Be specific — brand, model, size, colour"/>
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>{t.category} *</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6 }}>
              {CONFIG.categories.map(c=>(
                <button key={c.id} onClick={()=>setCat(c.id)}
                  style={{ padding:"8px 4px", border:`1.5px solid ${cat===c.id?B.c.gold:B.c.line}`, borderRadius:5, background:cat===c.id?B.c.goldLight:B.c.white, cursor:"pointer", fontSize:11, fontWeight:cat===c.id?700:400, color:cat===c.id?B.c.gold:B.c.slate, fontFamily:B.f.body, transition:"all 0.12s", textAlign:"center" }}>
                  {c[lang]||c.en}
                </button>
              ))}
            </div>
          </div>
          <Sel label={t.city} value={city} onChange={setCity} required options={[["","Select city..."],...territory.cities.map(c=>[c,c])]}/>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <Btn onClick={()=>setStep(2)} disabled={!title||!cat||!city}>{t.next}</Btn>
          </div>
        </>}

        {step===2&&<>
          <Field label={t.description} value={desc} onChange={setDesc} required maxLen={2000} rows={5} placeholder="Describe condition, specifications, reason for selling..."/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label={`${t.price} (${territory.currency})`} value={price} onChange={setPrice} type="number" placeholder="0" required={!isFree}/>
            <Sel label={t.condition} value={cond} onChange={setCond} options={[["new",t.new],["likeNew",t.likeNew],["good",t.good],["used",t.used]]}/>
          </div>
          <div style={{ display:"flex", gap:20, marginBottom:16 }}>
            {[[urgent,setUrgent,t.urgentCheck],[neg,setNeg,t.negCheck],[isFree,setIsFree,t.freeCheck]].map(([v,setter,label])=>(
              <label key={label} style={{ display:"flex", gap:8, alignItems:"center", cursor:"pointer", fontSize:13, color:B.c.slate, fontFamily:B.f.body }}>
                <input type="checkbox" checked={v} onChange={e=>setter(e.target.checked)}/>{label}
              </label>
            ))}
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>{t.photos}</label>
            <input type="file" accept="image/*" multiple onChange={handlePhoto} style={{ fontSize:13, color:B.c.muted, fontFamily:B.f.body }}/>
            {photos.length>0&&<div style={{ display:"flex", gap:8, marginTop:8 }}>
              {photos.map((p,i)=><img key={i} src={p} alt="" style={{ width:60, height:60, objectFit:"cover", borderRadius:5, border:`1px solid ${B.c.line}` }}/>)}
            </div>}
          </div>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <Btn onClick={()=>setStep(1)} variant="ghost">{t.back}</Btn>
            <Btn onClick={()=>setStep(3)} disabled={!desc}>{t.next}</Btn>
          </div>
        </>}

        {step===3&&<>
          <div style={{ background:B.c.surface, borderRadius:6, padding:16, marginBottom:20 }}>
            <h3 style={{ margin:"0 0 12px", color:B.c.ink, fontFamily:B.f.display }}>Summary</h3>
            {[[t.title,title],[t.category,CONFIG.categories.find(c=>c.id===cat)?.[lang]||""],[t.city,city],[t.price,isFree?t.free:`${price} ${territory.currency}`]].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${B.c.line}`, fontSize:13 }}>
                <span style={{ color:B.c.muted }}>{k}</span>
                <span style={{ fontWeight:600, color:B.c.ink }}>{v}</span>
              </div>
            ))}
          </div>
          <Field label={t.phone} value={phone} onChange={setPhone} required placeholder="+xxx xxx xxx xxx"/>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <Btn onClick={()=>setStep(2)} variant="ghost">{t.back}</Btn>
            <Btn onClick={submit} disabled={!phone} loading={loading}>{t.publish}</Btn>
          </div>
        </>}
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// LOGIN
// ─────────────────────────────────────────────────────────────
const Login = ({ lang, onClose, onLogin }) => {
  const [tab,setTab]=useState("in"); const [name,setName]=useState(""); const [email,setEmail]=useState(""); const [pwd,setPwd]=useState(""); const [conf,setConf]=useState(""); const [loading,setLoading]=useState(false);
  const t = T[lang]||T.en;

  const submit = async () => {
    setLoading(true);
    await new Promise(r=>setTimeout(r,900));
    const u = tab==="in"
      ? { id:"u1", name:"Demo User", email, role:"admin" }  // REPLACE WITH SUPABASE AUTH
      : { id:"u2", name, email, role:"user" };
    setLoading(false); onLogin(u); onClose();
    track("auth",{ type:tab });
  };

  return (
    <Modal onClose={onClose}>
      <ModalHead title={tab==="in"?t.signIn:t.register} onClose={onClose}/>
      <div style={{ padding:28 }}>
        <div style={{ display:"flex", border:`1px solid ${B.c.line}`, borderRadius:6, overflow:"hidden", marginBottom:24 }}>
          {[["in",t.signIn],["up",t.register]].map(([k,label])=>(
            <button key={k} onClick={()=>setTab(k)}
              style={{ flex:1, padding:"10px", border:"none", background:tab===k?B.c.ink:B.c.white, color:tab===k?B.c.white:B.c.slate, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:B.f.body }}>
              {label}
            </button>
          ))}
        </div>
        {tab==="up"&&<Field label={t.name} value={name} onChange={setName} required/>}
        <Field label={t.email} value={email} onChange={setEmail} type="email" required/>
        <Field label={t.password} value={pwd} onChange={setPwd} type="password" required/>
        {tab==="up"&&<Field label={t.confirmPwd} value={conf} onChange={setConf} type="password" error={conf&&pwd!==conf?"Passwords do not match":""}/>}
        <Btn onClick={submit} loading={loading} disabled={!email||!pwd||(tab==="up"&&(!name||pwd!==conf))} full>
          {tab==="in"?t.signIn:t.register}
        </Btn>
        <p style={{ textAlign:"center", fontSize:12, color:B.c.ghost, marginTop:12, fontFamily:B.f.body }}>
          Use any email and password to demo
        </p>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────
// AD CARD
// ─────────────────────────────────────────────────────────────
const AdCard = ({ ad, lang, onOpen, onFav, favs }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const t = T[lang]||T.en;
  const fav = favs.includes(ad.id);

  return (
    <article onClick={()=>onOpen(ad)}
      style={{ background:B.c.white, border:`1px solid ${B.c.line}`, borderRadius:8, overflow:"hidden", cursor:"pointer" }}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 8px 32px rgba(0,0,0,0.08)";e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.transform="none";}}>

      {/* Image */}
      <div style={{ position:"relative", height:192, background:B.c.surface }}>
        {!imgLoaded&&<div style={{ position:"absolute", inset:0, background:`linear-gradient(90deg,${B.c.surface} 25%,${B.c.line} 50%,${B.c.surface} 75%)`, backgroundSize:"200% 100%", animation:"vf-shimmer 1.4s infinite" }}/>}
        <img src={ad.img} alt={ad.title} onLoad={()=>setImgLoaded(true)}
          style={{ width:"100%", height:"100%", objectFit:"cover", opacity:imgLoaded?1:0, transition:"opacity 0.3s", display:"block" }}/>
        {/* Badges */}
        <div style={{ position:"absolute", top:10, left:10, display:"flex", gap:4, flexWrap:"wrap" }}>
          {ad.featured  && <span style={s.badge(B.c.gold, "#FBF0DC")}>{t.featured}</span>}
          {ad.urgent    && <span style={s.badge(B.c.red,  B.c.redBg)}>{t.urgent}</span>}
          {ad.free      && <span style={s.badge(B.c.green,B.c.greenBg)}>{t.free}</span>}
          {ad.pro       && <span style={s.badge(B.c.white,B.c.ink)}>{t.pro}</span>}
        </div>
        {/* Save */}
        <button onClick={e=>{e.stopPropagation();onFav(ad.id);}}
          style={{ position:"absolute", top:10, right:10, width:30, height:30, borderRadius:4, background:"rgba(255,255,255,0.96)", border:"none", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 1px 6px rgba(0,0,0,0.12)" }}>
          {fav ? "♥" : "♡"}
        </button>
      </div>

      {/* Info */}
      <div style={{ padding:"13px 15px" }}>
        <p style={{ margin:"0 0 5px", fontWeight:600, fontSize:13, color:B.c.ink, lineHeight:1.35, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{ad.title}</p>
        <p style={{ margin:"0 0 8px", fontSize:18, fontWeight:700, fontFamily:B.f.display, color:ad.free?B.c.green:B.c.ink }}>
          {ad.free?t.free:fmt(ad.price,ad.currency)}
          {ad.negotiable&&!ad.free&&<span style={{ fontSize:10, color:B.c.ghost, fontWeight:400, marginLeft:6 }}>{t.negotiable}</span>}
        </p>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:B.c.ghost }}>
          <span>{ad.city}</span>
          <span>{ago(ad.date,lang)}</span>
        </div>
      </div>
    </article>
  );
};

// ─────────────────────────────────────────────────────────────
// AD DETAIL
// ─────────────────────────────────────────────────────────────
const AdDetail = ({ ad, lang, territory, onBack, onFav, favs, user, addToast }) => {
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [showBoost, setShowBoost] = useState(false);
  const t = T[lang]||T.en;
  const fav = favs.includes(ad.id);
  const cat = CONFIG.categories.find(c=>c.id===ad.category);

  useEffect(()=>{
    setMeta(`${ad.title} — ${ad.city} — Vendifree`, ad.description.slice(0,160));
    track("listing_view",{ id:ad.id, territory:ad.territory, category:ad.category });
  },[ad]);

  return (
    <div>
      {showBoost&&<BoostModal ad={ad} territory={territory} lang={lang} onClose={()=>setShowBoost(false)} addToast={addToast}/>}

      <button onClick={onBack}
        style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer", color:B.c.muted, fontSize:13, marginBottom:28, padding:0, fontFamily:B.f.body }}>
        ← {t.back}
      </button>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:40, alignItems:"start" }}>
        {/* Left */}
        <div>
          <img src={ad.img} alt={ad.title} style={{ width:"100%", borderRadius:8, marginBottom:16, display:"block" }}/>
          <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
            {ad.featured  && <span style={s.badge(B.c.gold,  "#FBF0DC")}>{t.featured}</span>}
            {ad.urgent    && <span style={s.badge(B.c.red,   B.c.redBg)}>{t.urgent}</span>}
            {ad.free      && <span style={s.badge(B.c.green, B.c.greenBg)}>{t.free}</span>}
            {ad.negotiable&& <span style={s.badge(B.c.muted, B.c.surface)}>{t.negotiable}</span>}
            {ad.pro       && <span style={s.badge(B.c.white, B.c.ink)}>{t.pro}</span>}
          </div>
          <h1 style={{ fontFamily:B.f.display, fontSize:26, fontWeight:700, color:B.c.ink, margin:"0 0 8px", lineHeight:1.2 }}>{ad.title}</h1>
          <p style={{ fontFamily:B.f.display, fontSize:28, fontWeight:700, color:ad.free?B.c.green:B.c.gold, margin:"0 0 24px" }}>
            {ad.free?t.free:fmt(ad.price,ad.currency)}
            {!ad.free&&ad.currency!=="EUR"&&<span style={{ fontSize:14, color:B.c.ghost, fontWeight:400, marginLeft:12 }}>≈ €{(ad.price/CONFIG.rates[ad.currency]).toFixed(2)}</span>}
          </p>
          <p style={{ fontSize:15, color:B.c.slate, lineHeight:1.8, marginBottom:24 }}>{ad.description}</p>
          <HR/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {[[lang==="pt"?"Localização":lang==="fr"?"Localisation":"Location",`${ad.city}, ${territory?.name}`],[lang==="pt"?"Publicado":lang==="fr"?"Publié":"Posted",ago(ad.date,lang)],[lang==="pt"?"Visualizações":"Views",ad.views+1],[t.condition,t[ad.condition]||ad.condition],[lang==="pt"?"Categoria":lang==="fr"?"Catégorie":"Category",cat?.[lang]||cat?.en||""]].map(([k,v])=>(
              <div key={k}>
                <p style={{ margin:"0 0 2px", fontSize:11, color:B.c.ghost, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, fontFamily:B.f.body }}>{k}</p>
                <p style={{ margin:0, fontSize:14, color:B.c.ink, fontWeight:500 }}>{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right */}
        <div style={{ display:"grid", gap:10, position:"sticky", top:76 }}>
          <div style={s.card}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <div style={{ width:38, height:38, borderRadius:"50%", background:B.c.ink, color:B.c.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700 }}>{ad.seller[0]}</div>
              <div>
                <p style={{ margin:"0 0 1px", fontWeight:700, fontSize:14, color:B.c.ink }}>{ad.seller}</p>
                <p style={{ margin:0, fontSize:11, color:B.c.ghost }}>Member since 2023</p>
              </div>
            </div>
            <Btn onClick={()=>setPhoneVisible(v=>!v)} full style={{ marginBottom:8 }}>
              {phoneVisible?ad.phone:t.showPhone}
            </Btn>
            <Btn onClick={()=>{
              if (msgCount>=3){addToast("Message limit reached","warning");return;}
              setMsgCount(n=>n+1);addToast("Message sent","success");
            }} full variant="outline">
              {t.sendMsg} {msgCount>0?`(${3-msgCount})`:""} 
            </Btn>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <Btn onClick={()=>onFav(ad.id)} variant={fav?"gold":"ghost"} full sm>{fav?t.saved:t.saveAd}</Btn>
            <Btn onClick={()=>{navigator.clipboard.writeText(`https://vendifree.com/listing/${ad.id}`).catch(()=>{});addToast("Link copied","success");}} variant="ghost" full sm>{t.share}</Btn>
          </div>
          {user&&<Btn onClick={()=>setShowBoost(true)} variant="gold" full>{t.boost}</Btn>}
          <Btn onClick={()=>addToast("Report submitted","success")} variant="ghost" full sm>{t.report}</Btn>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AMBASSADOR PROGRAMME
// ─────────────────────────────────────────────────────────────
const Ambassador = ({ lang, territory, user, addToast }) => {
  const [users, setUsers] = useState(1000);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name:user?.name||"", email:user?.email||"", territory:territory?.code||"MT", city:"", phone:"", why:"", social:"", reach:"", agreed:false });
  const t = T[lang]||T.en;
  const earnings = Math.round(users*0.05*9.99*CONFIG.ambassadorFee);
  const isValid = form.name&&form.email&&form.city&&form.phone&&form.why.length>=50&&form.social&&form.reach&&form.agreed;

  const submit = async () => {
    setLoading(true);
    await new Promise(r=>setTimeout(r,1200));
    track("ambassador_apply",{ territory:form.territory });
    setLoading(false); setSubmitted(true);
    addToast(t.appReceived,"success");
  };

  return (
    <div>
      {/* Hero */}
      <div style={{ background:B.c.deep, color:B.c.white, borderRadius:10, padding:"72px 56px", marginBottom:40, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-60, right:-60, width:300, height:300, borderRadius:"50%", border:`1px solid rgba(184,137,42,0.2)` }}/>
        <div style={{ position:"absolute", bottom:-80, right:80, width:200, height:200, borderRadius:"50%", border:`1px solid rgba(184,137,42,0.1)` }}/>
        <div style={{ position:"relative", maxWidth:600 }}>
          <p style={{ margin:"0 0 12px", fontSize:10, fontWeight:800, letterSpacing:2, color:B.c.gold, textTransform:"uppercase", fontFamily:B.f.body }}>Ambassador Programme</p>
          <h1 style={{ fontFamily:B.f.display, fontSize:"clamp(28px,4vw,46px)", fontWeight:700, margin:"0 0 16px", lineHeight:1.1 }}>
            {t.ambHero}
          </h1>
          <p style={{ fontSize:16, opacity:0.65, marginBottom:36, lineHeight:1.6 }}>
            {lang==="pt"?"Sem investimento. Sem código. 2 a 3 horas por semana.":lang==="fr"?"Sans investissement. Sans code. 2 à 3 heures par semaine.":"No investment. No code. 2 to 3 hours per week."}
          </p>
          <div style={{ display:"flex", gap:40, marginBottom:40, flexWrap:"wrap" }}>
            {[["10",lang==="pt"?"Territórios":"Territories"],["25%","Commission"],["47",lang==="pt"?"Embaixadores":"Ambassadors"]].map(([val,lbl])=>(
              <div key={lbl}>
                <p style={{ margin:"0 0 2px", fontSize:32, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>{val}</p>
                <p style={{ margin:0, fontSize:10, opacity:0.5, textTransform:"uppercase", letterSpacing:1.5, fontFamily:B.f.body }}>{lbl}</p>
              </div>
            ))}
          </div>
          <Btn onClick={()=>setShowForm(true)} variant="gold">{t.applyNow}</Btn>
        </div>
      </div>

      {/* Steps */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:40 }}>
        {[
          { n:"01", en:"Apply in 2 minutes",        pt:"Candidata-te em 2 min",     fr:"Candidatez-vous en 2 min" },
          { n:"02", en:"Approved within 24 hours",  pt:"Aprovação em 24 horas",     fr:"Approbation sous 24h" },
          { n:"03", en:"Promote in your community", pt:"Promove na tua comunidade", fr:"Promouvez dans votre territoire" },
          { n:"04", en:"Receive 25% monthly",       pt:"Recebe 25% mensalmente",    fr:"Recevez 25% mensuellement" },
        ].map(step=>(
          <div key={step.n} style={s.card}>
            <p style={{ margin:"0 0 12px", fontSize:10, fontWeight:800, color:B.c.gold, letterSpacing:2, fontFamily:B.f.mono }}>{step.n}</p>
            <p style={{ margin:0, fontWeight:600, fontSize:14, color:B.c.ink, lineHeight:1.4 }}>{step[lang]||step.en}</p>
          </div>
        ))}
      </div>

      {/* Calculator */}
      <div style={{ background:B.c.deep, color:B.c.white, borderRadius:10, padding:"48px 56px", marginBottom:40 }}>
        <h2 style={{ fontFamily:B.f.display, margin:"0 0 8px" }}>{lang==="pt"?"Quanto podes ganhar?":lang==="fr"?"Combien pouvez-vous gagner?":"Estimate your earnings"}</h2>
        <p style={{ opacity:0.5, marginBottom:32, fontSize:13 }}>{lang==="pt"?"Utilizadores activos no teu território":lang==="fr"?"Utilisateurs actifs dans votre territoire":"Active users in your territory"}</p>
        <input type="range" min={100} max={10000} value={users} onChange={e=>setUsers(Number(e.target.value))}
          style={{ width:"100%", marginBottom:8, accentColor:B.c.gold }}/>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, opacity:0.4, marginBottom:32 }}>
          <span>100</span>
          <span style={{ opacity:1, fontSize:14, fontWeight:700, color:B.c.gold }}>{users.toLocaleString()} {lang==="pt"?"utilizadores":lang==="fr"?"utilisateurs":"users"}</span>
          <span>10 000</span>
        </div>
        <div style={{ textAlign:"center" }}>
          <p style={{ margin:"0 0 4px", fontSize:10, letterSpacing:2, opacity:0.5, textTransform:"uppercase", fontFamily:B.f.body }}>Estimated monthly earnings</p>
          <p style={{ margin:0, fontSize:56, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>€{earnings}</p>
        </div>
      </div>

      {/* Form */}
      {showForm&&!submitted&&(
        <div style={{ ...s.card, marginBottom:32 }}>
          <h2 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 24px" }}>{t.applyNow}</h2>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label={t.name} value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} required/>
            <Field label={t.email} value={form.email} onChange={v=>setForm(f=>({...f,email:v}))} type="email" required/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Sel label={t.territory} value={form.territory} onChange={v=>setForm(f=>({...f,territory:v}))} options={CONFIG.territories.map(t=>[t.code,t.name])} required/>
            <Field label={t.city} value={form.city} onChange={v=>setForm(f=>({...f,city:v}))} required/>
          </div>
          <Field label={t.phone} value={form.phone} onChange={v=>setForm(f=>({...f,phone:v}))} required/>
          <Field label={t.whyAmb} value={form.why} onChange={v=>setForm(f=>({...f,why:v}))} rows={4} maxLen={500} required
            error={form.why.length>0&&form.why.length<50?"Minimum 50 characters":""}/>
          <Field label={t.socialLink} value={form.social} onChange={v=>setForm(f=>({...f,social:v}))} placeholder="https://..." required/>
          <Sel label={t.reach} value={form.reach} onChange={v=>setForm(f=>({...f,reach:v}))} required
            options={[["","Select..."],["u500",t.u500],["500_2k",t["500_2k"]],["2k_10k",t["2k_10k"]],["o10k",t.o10k]]}/>
          <label style={{ display:"flex", gap:10, cursor:"pointer", marginBottom:20, fontSize:13, color:B.c.muted, fontFamily:B.f.body }}>
            <input type="checkbox" checked={form.agreed} onChange={e=>setForm(f=>({...f,agreed:e.target.checked}))} style={{ marginTop:2 }}/>
            {t.agreeTerms}
          </label>
          <Btn onClick={submit} disabled={!isValid} loading={loading} full>{t.submitApp}</Btn>
        </div>
      )}
      {submitted&&(
        <div style={{ background:B.c.greenBg, border:`1px solid ${B.c.green}`, borderRadius:8, padding:32, textAlign:"center" }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:B.c.green, color:B.c.white, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontSize:20 }}>✓</div>
          <p style={{ color:B.c.green, fontWeight:600, margin:0 }}>{t.appReceived}</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AMBASSADOR DASHBOARD
// ─────────────────────────────────────────────────────────────
const AmbDash = ({ lang, user, territory, addToast }) => {
  const [copied, setCopied] = useState("");
  const t = T[lang]||T.en;
  const refLink = `https://vendifree.com/ref/${user?.id||"demo"}`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(()=>{});
    setCopied(key); setTimeout(()=>setCopied(""),2000);
    addToast("Copied","success");
  };

  const templates = [
    { id:"fb_en",  ch:"Facebook (EN)", text:`I just joined the Vendifree ambassador programme in ${territory?.name}. Post your listings for free and reach local buyers: ${refLink}` },
    { id:"fb_pt",  ch:"Facebook (PT)", text:`Juntei-me ao programa de embaixadores Vendifree em ${territory?.name}. Publica anúncios gratuitamente: ${refLink}` },
    { id:"fb_fr",  ch:"Facebook (FR)", text:`J'ai rejoint le programme ambassadeur Vendifree à ${territory?.name}. Publiez vos annonces gratuitement: ${refLink}` },
    { id:"direct", ch:"Direct message", text:`Hi! I'm helping grow Vendifree in ${territory?.name} — a free local marketplace. Join here: ${refLink}` },
    { id:"whatsapp",ch:"WhatsApp",      text:`Check out Vendifree in ${territory?.name} — post anything for free, buy locally. My link: ${refLink}` },
  ];

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:32, flexWrap:"wrap" }}>
        <h1 style={{ fontFamily:B.f.display, color:B.c.ink, margin:0 }}>{t.ambDash}</h1>
        <span style={s.badge(B.c.gold,"#FBF0DC")}>Ambassador</span>
        <span style={{ fontSize:13, color:B.c.muted }}>{territory?.name}</span>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:32 }}>
        {[["Users recruited","145"],["Revenue generated","€420"],["Your commission","€105"],["Payments received","3"]].map(([lbl,val])=>(
          <div key={lbl} style={s.card}>
            <p style={{ margin:"0 0 4px", fontSize:24, fontWeight:700, fontFamily:B.f.display, color:B.c.ink }}>{val}</p>
            <p style={{ margin:0, fontSize:12, color:B.c.muted }}>{lbl}</p>
          </div>
        ))}
      </div>

      <div style={{ ...s.card, marginBottom:16 }}>
        <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 12px" }}>{t.refLink}</h3>
        <div style={{ display:"flex", gap:8 }}>
          <input value={refLink} readOnly style={{ ...s.input(), background:B.c.surface, color:B.c.muted, fontFamily:B.f.mono, fontSize:12, flex:1 }}/>
          <Btn onClick={()=>copy(refLink,"link")} variant="outline" sm>{copied==="link"?"Copied":t.copyLink}</Btn>
        </div>
      </div>

      <div style={{ ...s.card, marginBottom:16 }}>
        <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 16px" }}>{t.templates}</h3>
        <div style={{ display:"grid", gap:10 }}>
          {templates.map(tmpl=>(
            <div key={tmpl.id} style={{ border:`1px solid ${B.c.line}`, borderRadius:6, overflow:"hidden" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:B.c.surface, borderBottom:`1px solid ${B.c.line}` }}>
                <span style={{ fontSize:11, fontWeight:700, color:B.c.ink, textTransform:"uppercase", letterSpacing:0.5, fontFamily:B.f.body }}>{tmpl.ch}</span>
                <Btn onClick={()=>copy(tmpl.text,tmpl.id)} variant="ghost" sm>{copied===tmpl.id?"Copied":t.copy}</Btn>
              </div>
              <p style={{ padding:"12px 14px", fontSize:13, color:B.c.slate, margin:0, lineHeight:1.6 }}>{tmpl.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={s.card}>
        <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 16px" }}>{t.payHistory}</h3>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:`2px solid ${B.c.line}` }}>
              {["Date","Amount","Status"].map(h=><th key={h} style={{ textAlign:"left", padding:"8px 0", fontSize:10, fontWeight:700, color:B.c.muted, textTransform:"uppercase", letterSpacing:0.8, fontFamily:B.f.body }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {[["Jan 2024","€105","paid"],["Dec 2023","€87","paid"],["Nov 2023","€72","paid"]].map(([d,v,st])=>(
              <tr key={d} style={{ borderBottom:`1px solid ${B.c.surface}` }}>
                <td style={{ padding:"10px 0", fontSize:13, color:B.c.slate }}>{d}</td>
                <td style={{ padding:"10px 0", fontSize:13, fontWeight:700 }}>{v}</td>
                <td style={{ padding:"10px 0" }}><span style={s.badge(B.c.green,B.c.greenBg)}>{st}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SEO & MARKETING
// ─────────────────────────────────────────────────────────────
const SeoMarketing = ({ lang, territory, user, addToast }) => {
  const [selTerritory, setSelTerritory] = useState(territory?.code||"MT");
  const [selCat, setSelCat] = useState("electronics");
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [copied, setCopied] = useState("");
  const t = T[lang]||T.en;
  const terr = CONFIG.territories.find(t=>t.code===selTerritory);

  const generate = async () => {
    setGenerating(true);
    await new Promise(r=>setTimeout(r,1800));
    const cat = CONFIG.categories.find(c=>c.id===selCat);
    setContent({
      title: `Buy & Sell ${cat?.[lang]||cat?.en} in ${terr?.name} — Vendifree`,
      desc: `Find the best ${cat?.[lang]||cat?.en} listings in ${terr?.name}. Post for free. Trusted local marketplace.`,
      h1: `${cat?.[lang]||cat?.en} for sale in ${terr?.name}`,
      keywords: [`buy ${cat?.en} ${terr?.name}`,`sell ${cat?.en} ${terr?.name}`,`${cat?.en} classifieds ${terr?.name}`,`${terr?.name} marketplace`],
    });
    setGenerating(false);
    addToast("SEO content generated","success");
    track("seo_generated",{ territory:selTerritory, category:selCat });
    /* REPLACE WITH GEMINI:
       const { GoogleGenerativeAI } = await import('@google/generative-ai');
       const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_KEY);
       const model = genAI.getGenerativeModel({ model:'gemini-2.0-flash' });
       const result = await model.generateContent(`Generate SEO title, description, h1 and 4 keywords for a classifieds page for ${selCat} in ${terr?.name} in ${lang}. Return JSON only.`);
    */
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(()=>{});
    setCopied(key); setTimeout(()=>setCopied(""),2000);
    addToast("Copied","success");
  };

  const refLink = `https://vendifree.com/ref/${user?.id||"ambassador"}`;

  const socialTemplates = [
    { id:"fb",  ch:"Facebook", text:`${lang==="pt"?`Acabou de lançar o Vendifree em ${terr?.name}! Anúncios gratuitos. Compradores locais. Entra já`:lang==="fr"?`Vendifree vient de lancer à ${terr?.name}! Annonces gratuites. Acheteurs locaux. Rejoignez-nous`:`Vendifree just launched in ${terr?.name}! Free listings, local buyers. Join now`}: vendifree.com/${selTerritory.toLowerCase()}` },
    { id:"wa",  ch:"WhatsApp", text:`${lang==="pt"?`Olá! Partilho um novo marketplace gratuito para ${terr?.name}`:lang==="fr"?`Bonjour! Voici un nouveau marketplace gratuit pour ${terr?.name}`:`Hey! New free marketplace in ${terr?.name}`}: ${refLink}` },
    { id:"ref", ch:"Ambassador referral", text:`${lang==="pt"?`Olá! Sou embaixador Vendifree em ${terr?.name}. Publica os teus anúncios gratuitamente pelo meu link`:lang==="fr"?`Bonjour! Je suis ambassadeur Vendifree à ${terr?.name}. Publiez vos annonces gratuitement via mon lien`:`Hi! I'm a Vendifree ambassador in ${terr?.name}. Post your listings for free via my link`}: ${refLink}` },
    { id:"dm",  ch:"Direct recruitment", text:`${lang==="pt"?`Olá! Posso partilhar o teu anúncio no Vendifree, o novo marketplace local de ${terr?.name}? É gratuito e já temos compradores activos`:lang==="fr"?`Bonjour! Puis-je partager votre annonce sur Vendifree, le nouveau marketplace local de ${terr?.name}?`:`Hi! Can I list your ad on Vendifree, the new local marketplace in ${terr?.name}? It's free and we already have active buyers`}.` },
  ];

  const seoPages = CONFIG.territories.map(t=>({
    url:`vendifree.com/${t.code.toLowerCase()}`,
    name:t.name,
    indexed:["MT","CY","PT"].includes(t.code),
  }));

  return (
    <div>
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontFamily:B.f.display, fontSize:28, color:B.c.ink, margin:"0 0 8px" }}>{t.seoTitle}</h1>
        <p style={{ color:B.c.muted, fontSize:15, margin:0 }}>Automatic SEO and marketing tools for your territory and your ambassadors.</p>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:32 }}>
        {[[`${seoPages.length}`,"Territory pages","Auto-generated"],[`${seoPages.filter(p=>p.indexed).length}`,"Indexed","By Google"],["48","Keywords","Tracked across all territories"],["12.4","Avg. position","Google search"]].map(([val,lbl,sub])=>(
          <div key={lbl} style={s.card}>
            <p style={{ margin:"0 0 4px", fontSize:26, fontWeight:700, fontFamily:B.f.display, color:B.c.ink }}>{val}</p>
            <p style={{ margin:"0 0 2px", fontSize:13, fontWeight:700, color:B.c.ink }}>{lbl}</p>
            <p style={{ margin:0, fontSize:11, color:B.c.muted }}>{sub}</p>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, marginBottom:24 }}>
        {/* Territory pages */}
        <div style={s.card}>
          <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 8px" }}>Territory SEO Pages</h3>
          <p style={{ color:B.c.muted, fontSize:13, marginBottom:16 }}>Auto-generated landing pages per territory, with unique meta tags, schema markup and localised content.</p>
          <div>
            {seoPages.map(p=>(
              <div key={p.url} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${B.c.line}` }}>
                <div>
                  <p style={{ margin:"0 0 1px", fontSize:12, fontWeight:600, color:B.c.ink, fontFamily:B.f.mono }}>{p.url}</p>
                  <p style={{ margin:0, fontSize:11, color:B.c.muted }}>{p.name}</p>
                </div>
                <span style={s.badge(p.indexed?B.c.green:B.c.muted, p.indexed?B.c.greenBg:B.c.surface)}>
                  {p.indexed?"Indexed":"Pending"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* AI Generator */}
        <div style={s.card}>
          <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 4px" }}>AI Content Generator</h3>
          <p style={{ color:B.c.muted, fontSize:13, marginBottom:16 }}>Generate SEO-optimised titles, descriptions and keywords using Gemini AI.</p>
          <Sel label="Territory" value={selTerritory} onChange={setSelTerritory} options={CONFIG.territories.map(t=>[t.code,t.name])}/>
          <Sel label="Category" value={selCat} onChange={setSelCat} options={CONFIG.categories.map(c=>[c.id,c[lang]||c.en])}/>
          <Btn onClick={generate} loading={generating} full>Generate SEO content</Btn>
          {content&&(
            <div style={{ marginTop:16, padding:14, background:B.c.surface, borderRadius:6 }}>
              {[["Meta title",content.title,"title"],["Meta description",content.desc,"desc"],["H1",content.h1,"h1"]].map(([lbl,val,key])=>(
                <div key={key} style={{ marginBottom:12 }}>
                  <p style={{ ...s.label, marginBottom:4 }}>{lbl}</p>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                    <p style={{ margin:0, fontSize:12, color:B.c.slate, lineHeight:1.5, flex:1 }}>{val}</p>
                    <Btn onClick={()=>copy(val,key)} variant="ghost" sm>{copied===key?"Copied":t.copy}</Btn>
                  </div>
                </div>
              ))}
              <p style={s.label}>Keywords</p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                {content.keywords.map(k=>(
                  <span key={k} style={{ background:B.c.surface, border:`1px solid ${B.c.line}`, borderRadius:3, padding:"2px 8px", fontSize:11, color:B.c.slate, fontFamily:B.f.mono }}>{k}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Marketing kit */}
      <div style={s.card}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:16 }}>
          <div>
            <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 4px" }}>Ambassador Marketing Kit</h3>
            <p style={{ color:B.c.muted, fontSize:13, margin:0 }}>Ready-to-copy templates in all languages for ambassadors to promote their territory.</p>
          </div>
          <Sel label="" value={selTerritory} onChange={setSelTerritory} options={CONFIG.territories.map(t=>[t.code,t.name])}/>
        </div>
        <div style={{ display:"grid", gap:10 }}>
          {socialTemplates.map(tmpl=>(
            <div key={tmpl.id} style={{ border:`1px solid ${B.c.line}`, borderRadius:6, overflow:"hidden" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:B.c.surface, borderBottom:`1px solid ${B.c.line}` }}>
                <span style={{ fontSize:11, fontWeight:700, color:B.c.ink, textTransform:"uppercase", letterSpacing:0.5, fontFamily:B.f.body }}>{tmpl.ch}</span>
                <Btn onClick={()=>copy(tmpl.text,tmpl.id)} variant="ghost" sm>{copied===tmpl.id?"Copied":t.copy}</Btn>
              </div>
              <p style={{ padding:"12px 14px", fontSize:13, color:B.c.slate, margin:0, lineHeight:1.6 }}>{tmpl.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SAAS LANDING
// ─────────────────────────────────────────────────────────────
const Saas = ({ lang, onStart }) => {
  const [billing, setBilling] = useState("monthly");
  const [calcU, setCalcU] = useState(500);
  const [calcP, setCalcP] = useState(5);
  const [openFaq, setOpenFaq] = useState(null);
  const t = T[lang]||T.en;
  const rev = Math.round(79 + calcU*(calcP/100)*9.99*0.02);

  const faqs = [
    { q:{en:"Do I need to code?",pt:"Preciso de saber programar?",fr:"Dois-je savoir programmer?"}, a:{en:"No. We handle all technical setup. You configure and launch.",pt:"Não. Tratamos de toda a parte técnica. Só configuras e lanças.",fr:"Non. Nous gérons toute la partie technique. Vous configurez et lancez."} },
    { q:{en:"Can I use my own domain?",pt:"Posso usar o meu próprio domínio?",fr:"Puis-je utiliser mon propre domaine?"}, a:{en:"Yes. All plans include custom domain support.",pt:"Sim. Todos os planos incluem domínio próprio.",fr:"Oui. Tous les plans incluent un domaine personnalisé."} },
    { q:{en:"How does the revenue split work?",pt:"Como funciona a divisão da receita?",fr:"Comment fonctionne le partage?"}, a:{en:"Your Stripe account receives payments directly. Vendifree's fee is deducted automatically via Stripe Connect — no manual transfers.",pt:"A tua conta Stripe recebe os pagamentos directamente. A taxa Vendifree é deduzida automaticamente via Stripe Connect.",fr:"Votre compte Stripe reçoit les paiements directement. La commission est déduite automatiquement via Stripe Connect."} },
    { q:{en:"Can I cancel anytime?",pt:"Posso cancelar a qualquer momento?",fr:"Puis-je annuler à tout moment?"}, a:{en:"Yes. No contracts. Cancel with one click.",pt:"Sim. Sem contratos. Cancela com um clique.",fr:"Oui. Sans contrat. Annulez en un clic."} },
  ];

  return (
    <div>
      {/* Hero */}
      <div style={{ background:B.c.deep, color:B.c.white, borderRadius:10, padding:"80px 56px", marginBottom:56, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-80, right:-80, width:350, height:350, borderRadius:"50%", border:`1px solid rgba(184,137,42,0.15)` }}/>
        <div style={{ position:"absolute", bottom:-60, right:100, width:220, height:220, borderRadius:"50%", border:`1px solid rgba(184,137,42,0.08)` }}/>
        <div style={{ position:"relative", maxWidth:620 }}>
          <p style={{ margin:"0 0 14px", fontSize:10, fontWeight:800, letterSpacing:2, color:B.c.gold, textTransform:"uppercase", fontFamily:B.f.body }}>Vendifree Platform</p>
          <h1 style={{ fontFamily:B.f.display, fontSize:"clamp(28px,4vw,50px)", fontWeight:700, margin:"0 0 16px", lineHeight:1.1 }}>
            {lang==="pt"?"Lança a tua própria plataforma de classificados em minutos.":lang==="fr"?"Lancez votre propre plateforme d'annonces en quelques minutes.":"Launch your own classifieds platform in minutes."}
          </h1>
          <p style={{ fontSize:17, opacity:0.6, marginBottom:36, lineHeight:1.6 }}>
            {lang==="pt"?"Sem código. Sem servidor. Sem conhecimentos técnicos.":lang==="fr"?"Sans code. Sans serveur. Sans compétences techniques.":"No code. No server. No technical knowledge required."}
          </p>
          <div style={{ display:"flex", gap:12 }}>
            <Btn onClick={onStart} variant="gold">{t.startTrial}</Btn>
            <Btn variant="white" onClick={()=>{}}>Watch 2-min demo</Btn>
          </div>
        </div>
      </div>

      {/* Revenue calculator */}
      <div style={{ background:B.c.deep, color:B.c.white, borderRadius:10, padding:"48px 56px", marginBottom:56 }}>
        <h2 style={{ fontFamily:B.f.display, textAlign:"center", margin:"0 0 8px" }}>
          {lang==="pt"?"Quanto podes ganhar?":lang==="fr"?"Combien pouvez-vous gagner?":"Estimate your revenue"}
        </h2>
        <p style={{ textAlign:"center", opacity:0.4, fontSize:13, marginBottom:36 }}>Conservative estimates based on real platform data.</p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:40, marginBottom:32 }}>
          <div>
            <p style={{ margin:"0 0 8px", fontSize:11, opacity:0.5, textTransform:"uppercase", letterSpacing:1.2, fontFamily:B.f.body }}>Active users: <strong style={{ opacity:1 }}>{calcU.toLocaleString()}</strong></p>
            <input type="range" min={100} max={10000} value={calcU} onChange={e=>setCalcU(Number(e.target.value))} style={{ width:"100%", accentColor:B.c.gold }}/>
          </div>
          <div>
            <p style={{ margin:"0 0 8px", fontSize:11, opacity:0.5, textTransform:"uppercase", letterSpacing:1.2, fontFamily:B.f.body }}>Paying users: <strong style={{ opacity:1 }}>{calcP}%</strong></p>
            <input type="range" min={1} max={20} value={calcP} onChange={e=>setCalcP(Number(e.target.value))} style={{ width:"100%", accentColor:B.c.gold }}/>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, textAlign:"center" }}>
          {[[lang==="pt"?"A tua receita mensal":"Your monthly revenue",`€${rev}`,"base fee + 2% transactions"],[lang==="pt"?"O cliente ganha":"Client earns",`€${Math.round(calcU*(calcP/100)*9.99)}`,"from boosts & pro plans"],[lang==="pt"?"Lucro líquido":"Net profit",`€${Math.max(0,rev-5)}`,"after server costs"]].map(([lbl,val,sub])=>(
            <div key={lbl} style={{ padding:24, background:"rgba(255,255,255,0.04)", borderRadius:8, border:"1px solid rgba(255,255,255,0.07)" }}>
              <p style={{ margin:"0 0 6px", fontSize:10, opacity:0.5, textTransform:"uppercase", letterSpacing:1.2, fontFamily:B.f.body }}>{lbl}</p>
              <p style={{ margin:"0 0 4px", fontSize:34, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>{val}</p>
              <p style={{ margin:0, fontSize:11, opacity:0.35 }}>{sub}</p>
            </div>
          ))}
        </div>
        <p style={{ textAlign:"center", opacity:0.3, fontSize:11, marginTop:20, fontFamily:B.f.mono }}>
          The more your clients earn, the more you earn — automatically via Stripe Connect
        </p>
      </div>

      {/* Plans */}
      <h2 style={{ fontFamily:B.f.display, color:B.c.ink, textAlign:"center", margin:"0 0 8px" }}>{t.choosePlan}</h2>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:32 }}>
        <div style={{ display:"flex", border:`1px solid ${B.c.line}`, borderRadius:6, overflow:"hidden" }}>
          {[["monthly",t.monthly],["annual",`${t.annual} — ${t.save20}`]].map(([v,lbl])=>(
            <button key={v} onClick={()=>setBilling(v)}
              style={{ padding:"9px 20px", border:"none", background:billing===v?B.c.ink:B.c.white, color:billing===v?B.c.white:B.c.slate, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:B.f.body }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20, marginBottom:56 }}>
        {CONFIG.saasPlans.map(plan=>(
          <div key={plan.id} style={{ border:`2px solid ${plan.popular?B.c.gold:B.c.line}`, borderRadius:8, padding:28, position:"relative", background:B.c.white }}>
            {plan.popular&&<span style={{ ...s.badge(B.c.white,B.c.gold), position:"absolute", top:-11, left:"50%", transform:"translateX(-50%)", padding:"4px 14px" }}>Most popular</span>}
            <p style={{ margin:"0 0 4px", fontWeight:700, fontSize:15, color:B.c.ink }}>{plan.name}</p>
            <p style={{ margin:"0 0 2px", fontSize:10, color:B.c.muted, fontFamily:B.f.mono }}>{plan.limits}</p>
            <p style={{ margin:"12px 0 0", fontSize:34, fontWeight:700, fontFamily:B.f.display, color:B.c.ink }}>
              €{billing==="monthly"?plan.monthly:Math.round(plan.annual/12)}
              <span style={{ fontSize:13, fontWeight:400, color:B.c.muted }}>/mo</span>
            </p>
            <div style={{ display:"inline-flex", alignItems:"center", background:B.c.goldLight, border:`1px solid ${B.c.gold}`, borderRadius:3, padding:"3px 10px", fontSize:10, fontWeight:800, color:B.c.gold, letterSpacing:0.8, margin:"8px 0 16px" }}>
              + {plan.fee}% per transaction
            </div>
            <ul style={{ listStyle:"none", padding:0, margin:"0 0 24px", display:"grid", gap:7 }}>
              {(plan.features[lang]||plan.features.en).map((f,i)=>(
                <li key={i} style={{ fontSize:13, color:B.c.slate, display:"flex", gap:8, alignItems:"flex-start" }}>
                  <span style={{ color:B.c.green, fontWeight:700, marginTop:1, flexShrink:0 }}>✓</span>{f}
                </li>
              ))}
            </ul>
            <Btn onClick={onStart} full variant={plan.popular?"gold":"outline"}>{t.startTrial}</Btn>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <h2 style={{ fontFamily:B.f.display, color:B.c.ink, textAlign:"center", margin:"0 0 24px" }}>FAQ</h2>
      <div style={{ maxWidth:600, margin:"0 auto 56px", display:"grid", gap:6 }}>
        {faqs.map((item,i)=>(
          <div key={i} style={{ border:`1px solid ${B.c.line}`, borderRadius:6, overflow:"hidden" }}>
            <button onClick={()=>setOpenFaq(openFaq===i?null:i)}
              style={{ width:"100%", padding:"15px 20px", background:B.c.white, border:"none", textAlign:"left", fontWeight:600, fontSize:14, color:B.c.ink, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", fontFamily:B.f.body }}>
              {item.q[lang]||item.q.en}
              <span style={{ fontSize:9, opacity:0.4, transform:openFaq===i?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▼</span>
            </button>
            {openFaq===i&&<div style={{ padding:"0 20px 16px", fontSize:14, color:B.c.muted, lineHeight:1.7 }}>{item.a[lang]||item.a.en}</div>}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{ background:B.c.gold, borderRadius:10, padding:"64px 56px", textAlign:"center" }}>
        <h2 style={{ fontFamily:B.f.display, fontSize:34, color:B.c.deep, margin:"0 0 12px" }}>
          {lang==="pt"?"Lança a tua plataforma hoje.":lang==="fr"?"Lancez votre plateforme aujourd'hui.":"Launch your platform today."}
        </h2>
        <p style={{ color:B.c.deep, opacity:0.7, marginBottom:28, fontSize:16 }}>
          {lang==="pt"?"Sem código. Sem contratos. Cancela quando quiseres.":lang==="fr"?"Sans code. Sans contrat. Annulez quand vous voulez.":"No code. No contracts. Cancel anytime."}
        </p>
        <Btn onClick={onStart} variant="primary">{t.startTrial}</Btn>
        <p style={{ fontSize:12, color:B.c.deep, opacity:0.4, marginTop:12 }}>14-day free trial — no credit card required</p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ADMIN
// ─────────────────────────────────────────────────────────────
const Admin = ({ lang, ads, addToast }) => {
  const [apps, setApps] = useState(MOCK_AMB_APPS);
  const [ambs] = useState(MOCK_ACTIVE_AMB);
  const [tab, setTab] = useState("overview");
  const t = T[lang]||T.en;
  const tabs = [["overview",t.adminOverview],["ambassadors",t.adminAmbassadors],["listings",t.adminListings]];
  const bars = [180,220,195,310,280,340,290];
  const maxBar = Math.max(...bars);

  return (
    <div>
      <h1 style={{ fontFamily:B.f.display, color:B.c.ink, marginBottom:24 }}>{t.adminPanel}</h1>
      <div style={{ display:"flex", gap:4, borderBottom:`2px solid ${B.c.line}`, marginBottom:28 }}>
        {tabs.map(([v,lbl])=>(
          <button key={v} onClick={()=>setTab(v)}
            style={{ padding:"10px 16px", border:"none", background:"none", color:tab===v?B.c.gold:B.c.muted, fontWeight:tab===v?700:500, fontSize:13, cursor:"pointer", borderBottom:`2px solid ${tab===v?B.c.gold:"transparent"}`, marginBottom:-2, fontFamily:B.f.body }}>
            {lbl}
          </button>
        ))}
      </div>

      {tab==="overview"&&<>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:32 }}>
          {[[`${ads.length}`,"Listings"],["284","Registered users"],["€2 340","Revenue this month"],["10","Territories"]].map(([val,lbl])=>(
            <div key={lbl} style={s.card}>
              <p style={{ margin:"0 0 4px", fontSize:26, fontWeight:700, fontFamily:B.f.display, color:B.c.ink }}>{val}</p>
              <p style={{ margin:0, fontSize:12, color:B.c.muted }}>{lbl}</p>
            </div>
          ))}
        </div>
        <div style={s.card}>
          <h3 style={{ fontFamily:B.f.display, color:B.c.ink, margin:"0 0 20px" }}>Revenue — last 7 days</h3>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", height:110, paddingBottom:4 }}>
            {bars.map((v,i)=>(
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:9, color:B.c.ghost, fontFamily:B.f.mono }}>€{v}</span>
                <div style={{ width:"100%", height:`${(v/maxBar)*90}px`, background:B.c.ink, borderRadius:"2px 2px 0 0" }}/>
              </div>
            ))}
          </div>
        </div>
      </>}

      {tab==="ambassadors"&&<>
        <h3 style={{ fontFamily:B.f.display, color:B.c.ink, marginBottom:16 }}>{t.pendingApps}</h3>
        <div style={{ display:"grid", gap:12, marginBottom:32 }}>
          {apps.filter(a=>a.status==="pending").length===0&&<p style={{ color:B.c.ghost, textAlign:"center", padding:24 }}>No pending applications.</p>}
          {apps.filter(a=>a.status==="pending").map(app=>(
            <div key={app.id} style={s.card}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <p style={{ margin:"0 0 2px", fontWeight:700, fontSize:14, color:B.c.ink }}>{app.name}</p>
                  <p style={{ margin:0, fontSize:12, color:B.c.muted }}>{app.email} — {CONFIG.territories.find(t=>t.code===app.territory)?.name}, {app.city}</p>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <Btn onClick={()=>{setApps(p=>p.map(a=>a.id===app.id?{...a,status:"approved"}:a));addToast("Ambassador approved","success");}} sm variant="success">{t.approve}</Btn>
                  <Btn onClick={()=>{setApps(p=>p.filter(a=>a.id!==app.id));addToast("Application declined","success");}} sm variant="danger">{t.reject}</Btn>
                </div>
              </div>
              <p style={{ margin:0, fontSize:13, color:B.c.slate, lineHeight:1.6 }}>{app.why}</p>
            </div>
          ))}
        </div>
        <h3 style={{ fontFamily:B.f.display, color:B.c.ink, marginBottom:16 }}>{t.activeAmb}</h3>
        <div style={{ ...s.card, padding:0, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:B.c.surface }}>
                {["Name","Territory","Users","Revenue","Commission",""].map(h=><th key={h} style={{ textAlign:"left", padding:"12px 16px", fontSize:10, fontWeight:700, color:B.c.muted, textTransform:"uppercase", letterSpacing:0.8, fontFamily:B.f.body }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {ambs.map(a=>(
                <tr key={a.id} style={{ borderTop:`1px solid ${B.c.line}` }}>
                  <td style={{ padding:"12px 16px", fontSize:13, fontWeight:600, color:B.c.ink }}>{a.name}</td>
                  <td style={{ padding:"12px 16px", fontSize:13, color:B.c.muted }}>{CONFIG.territories.find(t=>t.code===a.territory)?.name}</td>
                  <td style={{ padding:"12px 16px", fontSize:13 }}>{a.users}</td>
                  <td style={{ padding:"12px 16px", fontSize:13 }}>€{a.revenue}</td>
                  <td style={{ padding:"12px 16px", fontSize:14, fontWeight:700, color:B.c.gold }}>€{a.commission}</td>
                  <td style={{ padding:"12px 16px" }}><Btn onClick={()=>addToast(`Payment sent to ${a.name}`,"success")} sm variant="outline">{t.markPaid}</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ background:B.c.goldLight, border:`1px solid ${B.c.gold}`, borderRadius:6, padding:16, marginTop:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:600, fontSize:13, color:B.c.ink }}>Total commissions payable</span>
          <span style={{ fontSize:22, fontWeight:700, fontFamily:B.f.display, color:B.c.gold }}>€{ambs.reduce((s,a)=>s+a.commission,0)}</span>
        </div>
      </>}

      {tab==="listings"&&(
        <div style={{ display:"grid", gap:8 }}>
          {ads.slice(0,12).map(ad=>(
            <div key={ad.id} style={{ ...s.card, display:"flex", gap:12, alignItems:"center", padding:"12px 16px" }}>
              <img src={ad.img} alt="" style={{ width:50, height:50, objectFit:"cover", borderRadius:5, flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <p style={{ margin:"0 0 2px", fontWeight:600, fontSize:13, color:B.c.ink }}>{ad.title}</p>
                <p style={{ margin:0, fontSize:11, color:B.c.muted }}>{ad.city} — {fmt(ad.price,ad.currency)} — {ad.views} views</p>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>addToast("Listing featured","success")} sm variant="outline">Feature</Btn>
                <Btn onClick={()=>addToast("Listing removed","success")} sm variant="danger">Remove</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [territory, setTerritory] = useState(CONFIG.territories[0]);
  const [lang,      setLang]      = useState("en");
  const [view,      setView]      = useState("home");
  const [ads,       setAds]       = useState(MOCK_ADS);
  const [favs,      setFavs]      = useState([]);
  const [user,      setUser]      = useState(null);
  const [selAd,     setSelAd]     = useState(null);
  const [query,     setQuery]     = useState("");
  const [selCat,    setSelCat]    = useState("");
  const [sortBy,    setSortBy]    = useState("newest");
  const [toasts,    setToasts]    = useState([]);
  const [page,      setPage]      = useState(1);
  const [showPost,  setShowPost]  = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showTerr,  setShowTerr]  = useState(false);
  const [showUser,  setShowUser]  = useState(false);
  const PER_PAGE = 12;

  const addToast = useCallback((msg, type="success") => {
    const id = Date.now();
    setToasts(p=>[...p,{id,msg,type}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3500);
  },[]);

  const t = T[lang]||T.en;

  const handleTerritory = terr => {
    setTerritory(terr); setLang(terr.language); setShowTerr(false);
    setMeta(`${B.name} — ${terr.name}`, B.tagline[terr.language]||B.tagline.en);
    track("territory",{ code:terr.code });
  };

  const handleFav = id => setFavs(p=>p.includes(id)?p.filter(f=>f!==id):[...p,id]);
  const handlePost = ad => { setAds(p=>[ad,...p]); setShowPost(false); addToast(t.posted); };

  const filtered = ads
    .filter(ad=>(!query||ad.title.toLowerCase().includes(query.toLowerCase()))&&(!selCat||ad.category===selCat))
    .sort((a,b)=>sortBy==="price"?a.price-b.price:sortBy==="priceHigh"?b.price-a.price:new Date(b.date)-new Date(a.date));

  const paged = filtered.slice(0, page*PER_PAGE);

  useEffect(()=>{
    setMeta(`${B.name} — ${territory.name}`, B.tagline[lang]||B.tagline.en);
  },[territory,lang]);

  const navItems = [
    ["ambassador","ambassador"],
    ["saas","launchPlatform"],
    ["seo-marketing","seoMarketing"],
  ];

  return (
    <div style={{ fontFamily:B.f.body, minHeight:"100vh", background:B.c.surface, color:B.c.ink }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes vf-spin    { to { transform:rotate(360deg); } }
        @keyframes vf-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes vf-in      { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:none} }
        * { box-sizing:border-box; margin:0; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-thumb { background:${B.c.line}; border-radius:2px; }
        article { transition:box-shadow 0.2s, transform 0.2s; }
        input[type=range] { height:3px; }
      `}</style>

      {/* ── HEADER ── */}
      <header style={{ background:B.c.white, borderBottom:`1px solid ${B.c.line}`, padding:"0 32px", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ maxWidth:1360, margin:"0 auto", display:"flex", alignItems:"center", gap:18, height:58 }}>

          {/* Logo */}
          <button onClick={()=>{setView("home");setSelAd(null);}}
            style={{ background:"none", border:"none", cursor:"pointer", fontFamily:B.f.display, fontSize:20, fontWeight:700, color:B.c.ink, letterSpacing:-0.5, padding:0, flexShrink:0 }}>
            Vendifree
          </button>

          {/* Search */}
          <div style={{ flex:1, maxWidth:400, position:"relative" }}>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder={t.search}
              style={{ ...s.input(), height:36, paddingLeft:34, fontSize:13 }}
              onFocus={e=>e.target.style.borderColor=B.c.gold}
              onBlur={e=>e.target.style.borderColor=B.c.line}/>
            <span style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:B.c.ghost, fontSize:14, pointerEvents:"none" }}>⌕</span>
          </div>

          {/* Territory picker */}
          <div style={{ position:"relative" }}>
            <button onClick={()=>setShowTerr(v=>!v)}
              style={{ background:B.c.surface, border:`1px solid ${B.c.line}`, color:B.c.slate, padding:"6px 12px", borderRadius:5, cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:B.f.body, display:"flex", alignItems:"center", gap:6, letterSpacing:0.3 }}>
              {territory.name}
              <span style={{ fontSize:8, color:B.c.ghost }}>▼</span>
            </button>
            {showTerr&&(
              <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, background:B.c.white, border:`1px solid ${B.c.line}`, borderRadius:8, boxShadow:"0 8px 40px rgba(0,0,0,0.1)", minWidth:210, zIndex:200 }}>
                {CONFIG.territories.map(terr=>(
                  <button key={terr.code} onClick={()=>handleTerritory(terr)}
                    style={{ display:"block", width:"100%", padding:"9px 16px", background:territory.code===terr.code?B.c.surface:B.c.white, border:"none", borderBottom:`1px solid ${B.c.surface}`, cursor:"pointer", fontSize:13, color:B.c.ink, textAlign:"left", fontWeight:territory.code===terr.code?700:400, fontFamily:B.f.body }}>
                    {terr.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Language */}
          <select value={lang} onChange={e=>setLang(e.target.value)}
            style={{ background:B.c.surface, border:`1px solid ${B.c.line}`, color:B.c.slate, padding:"6px 10px", borderRadius:5, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:B.f.body, letterSpacing:1 }}>
            {["en","pt","fr"].map(l=><option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>

          {/* Nav */}
          <nav style={{ display:"flex", gap:2 }}>
            {navItems.map(([v,key])=>(
              <button key={v} onClick={()=>{setView(v);setSelAd(null);}}
                style={{ background:"none", border:"none", color:view===v?B.c.ink:B.c.muted, fontWeight:view===v?700:500, fontSize:12, cursor:"pointer", fontFamily:B.f.body, padding:"6px 10px", borderRadius:4, whiteSpace:"nowrap", letterSpacing:0.2 }}>
                {t[key]}
              </button>
            ))}
          </nav>

          {/* Auth */}
          {user?(
            <div style={{ position:"relative" }}>
              <button onClick={()=>setShowUser(v=>!v)}
                style={{ width:32, height:32, borderRadius:"50%", background:B.c.ink, color:B.c.white, border:"none", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:B.f.body, flexShrink:0 }}>
                {user.name[0]}
              </button>
              {showUser&&(
                <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, background:B.c.white, border:`1px solid ${B.c.line}`, borderRadius:8, boxShadow:"0 8px 40px rgba(0,0,0,0.1)", minWidth:190, zIndex:200 }}>
                  {[["my-listings",t.myListings],["saved",t.saved],["ambassador-dash",t.ambDash],["admin",t.adminPanel]].map(([v,lbl])=>(
                    <button key={v} onClick={()=>{setView(v);setSelAd(null);setShowUser(false);}}
                      style={{ display:"block", width:"100%", padding:"10px 16px", background:B.c.white, border:"none", borderBottom:`1px solid ${B.c.surface}`, textAlign:"left", fontSize:13, color:B.c.ink, cursor:"pointer", fontFamily:B.f.body }}>
                      {lbl}
                    </button>
                  ))}
                  <button onClick={()=>{setUser(null);setShowUser(false);addToast(t.signOut);}}
                    style={{ display:"block", width:"100%", padding:"10px 16px", background:B.c.redBg, border:"none", textAlign:"left", fontSize:13, color:B.c.red, cursor:"pointer", fontWeight:700, fontFamily:B.f.body }}>
                    {t.signOut}
                  </button>
                </div>
              )}
            </div>
          ):(
            <button onClick={()=>setShowLogin(true)}
              style={{ background:"none", border:`1px solid ${B.c.line}`, color:B.c.ink, padding:"6px 14px", borderRadius:5, cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:B.f.body, letterSpacing:0.3 }}>
              {t.signIn}
            </button>
          )}

          <Btn onClick={()=>user?setShowPost(true):setShowLogin(true)} sm style={{ flexShrink:0 }}>
            {t.post}
          </Btn>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main style={{ maxWidth:1360, margin:"0 auto", padding:"32px 32px 80px" }}>

        {/* HOME */}
        {view==="home"&&<>
          {/* Hero bar */}
          <div style={{ background:B.c.deep, color:B.c.white, borderRadius:8, padding:"44px 48px", marginBottom:28, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-50, right:-50, width:250, height:250, borderRadius:"50%", border:`1px solid rgba(184,137,42,0.15)` }}/>
            <div style={{ position:"relative" }}>
              <p style={{ margin:"0 0 8px", fontSize:10, fontWeight:800, letterSpacing:2, color:B.c.gold, textTransform:"uppercase", fontFamily:B.f.body }}>{territory.name}</p>
              <h1 style={{ fontFamily:B.f.display, fontSize:"clamp(22px,3vw,36px)", fontWeight:700, margin:"0 0 8px", lineHeight:1.1 }}>
                {B.tagline[lang]||B.tagline.en}
              </h1>
              <p style={{ opacity:0.5, fontSize:13, margin:0 }}>
                {filtered.length} {lang==="pt"?"anúncios activos":lang==="fr"?"annonces actives":"active listings"} — {territory.name}
              </p>
            </div>
          </div>

          {/* Category filter */}
          <div style={{ display:"flex", gap:5, marginBottom:18, overflowX:"auto", paddingBottom:2 }}>
            <button onClick={()=>setSelCat("")}
              style={{ padding:"6px 14px", borderRadius:4, border:`1.5px solid ${!selCat?B.c.ink:B.c.line}`, background:!selCat?B.c.ink:B.c.white, color:!selCat?B.c.white:B.c.slate, fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap", fontFamily:B.f.body, letterSpacing:0.4 }}>
              {t.allCats}
            </button>
            {CONFIG.categories.map(cat=>(
              <button key={cat.id} onClick={()=>setSelCat(cat.id===selCat?"":cat.id)}
                style={{ padding:"6px 12px", borderRadius:4, border:`1.5px solid ${selCat===cat.id?B.c.gold:B.c.line}`, background:selCat===cat.id?B.c.goldLight:B.c.white, color:selCat===cat.id?B.c.gold:B.c.slate, fontWeight:selCat===cat.id?700:500, fontSize:11, cursor:"pointer", whiteSpace:"nowrap", fontFamily:B.f.body }}>
                {cat[lang]||cat.en}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <span style={{ fontSize:12, color:B.c.muted }}>{filtered.length} results in {territory.name}</span>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{ padding:"6px 12px", border:`1px solid ${B.c.line}`, borderRadius:5, fontSize:12, color:B.c.slate, fontFamily:B.f.body, background:B.c.white, cursor:"pointer" }}>
              <option value="newest">{t.newest}</option>
              <option value="price">{t.priceLow}</option>
              <option value="priceHigh">{t.priceHigh}</option>
            </select>
          </div>

          {/* Grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(236px,1fr))", gap:18, marginBottom:28 }}>
            {paged.map(ad=>(
              <AdCard key={ad.id} ad={ad} lang={lang} favs={favs}
                onOpen={ad=>{setSelAd(ad);setView("detail");setPage(1);}}
                onFav={handleFav}/>
            ))}
          </div>

          {paged.length<filtered.length&&(
            <div style={{ textAlign:"center" }}>
              <Btn onClick={()=>setPage(p=>p+1)} variant="outline">{t.loadMore} ({filtered.length-paged.length})</Btn>
            </div>
          )}
        </>}

        {/* DETAIL */}
        {view==="detail"&&selAd&&(
          <AdDetail ad={selAd} lang={lang} territory={territory} favs={favs} user={user}
            onBack={()=>{setView("home");setSelAd(null);}} onFav={handleFav} addToast={addToast}/>
        )}

        {/* SAVED */}
        {view==="saved"&&(
          <div>
            <h1 style={{ fontFamily:B.f.display, color:B.c.ink, marginBottom:24 }}>{t.saved}</h1>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(236px,1fr))", gap:18 }}>
              {ads.filter(a=>favs.includes(a.id)).map(ad=>(
                <AdCard key={ad.id} ad={ad} lang={lang} favs={favs}
                  onOpen={ad=>{setSelAd(ad);setView("detail");}} onFav={handleFav}/>
              ))}
            </div>
          </div>
        )}

        {/* MY LISTINGS */}
        {view==="my-listings"&&(
          <div>
            <h1 style={{ fontFamily:B.f.display, color:B.c.ink, marginBottom:24 }}>{t.myListings}</h1>
            <div style={{ display:"grid", gap:8 }}>
              {ads.filter(a=>a.seller===user?.name).map(ad=>(
                <div key={ad.id} style={{ ...s.card, display:"flex", gap:12, alignItems:"center", padding:"12px 16px" }}>
                  <img src={ad.img} alt="" style={{ width:50, height:50, objectFit:"cover", borderRadius:5 }}/>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:"0 0 2px", fontWeight:600, fontSize:13, color:B.c.ink }}>{ad.title}</p>
                    <p style={{ margin:0, fontSize:11, color:B.c.muted }}>{fmt(ad.price,ad.currency)} — {ad.views} views</p>
                  </div>
                  <Btn onClick={()=>addToast("Listing removed","success")} sm variant="danger">Remove</Btn>
                </div>
              ))}
            </div>
          </div>
        )}

        {view==="ambassador"      && <Ambassador   lang={lang} territory={territory} user={user} addToast={addToast}/>}
        {view==="ambassador-dash" && <AmbDash      lang={lang} user={user} territory={territory} addToast={addToast}/>}
        {view==="seo-marketing"   && <SeoMarketing lang={lang} territory={territory} user={user} addToast={addToast}/>}
        {view==="saas"            && <Saas         lang={lang} onStart={()=>addToast("Opening trial setup...","success")}/>}
        {view==="admin"           && <Admin        lang={lang} ads={ads} addToast={addToast}/>}

      </main>

      {/* ── FOOTER ── */}
      <footer style={{ background:B.c.deep, color:B.c.white, padding:"48px 32px 28px" }}>
        <div style={{ maxWidth:1360, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:32, marginBottom:40 }}>
            <div>
              <p style={{ fontFamily:B.f.display, fontSize:18, fontWeight:700, marginBottom:8, margin:"0 0 8px" }}>Vendifree</p>
              <p style={{ opacity:0.45, fontSize:13, lineHeight:1.7 }}>{B.tagline[lang]||B.tagline.en}</p>
            </div>
            <div>
              <p style={{ fontSize:9, fontWeight:800, letterSpacing:1.8, opacity:0.4, marginBottom:14, textTransform:"uppercase" }}>Categories</p>
              {CONFIG.categories.slice(0,6).map(c=>(
                <button key={c.id} onClick={()=>{setSelCat(c.id);setView("home");}}
                  style={{ display:"block", color:"rgba(255,255,255,0.4)", background:"none", border:"none", cursor:"pointer", fontSize:12, padding:"3px 0", fontFamily:B.f.body }}>
                  {c[lang]||c.en}
                </button>
              ))}
            </div>
            <div>
              <p style={{ fontSize:9, fontWeight:800, letterSpacing:1.8, opacity:0.4, marginBottom:14, textTransform:"uppercase" }}>Platform</p>
              {[["ambassador",t.ambassador],["saas",t.launchPlatform],["seo-marketing",t.seoMarketing]].map(([v,lbl])=>(
                <button key={v} onClick={()=>setView(v)}
                  style={{ display:"block", color:"rgba(255,255,255,0.4)", background:"none", border:"none", cursor:"pointer", fontSize:12, padding:"3px 0", fontFamily:B.f.body }}>
                  {lbl}
                </button>
              ))}
            </div>
            <div>
              <p style={{ fontSize:9, fontWeight:800, letterSpacing:1.8, opacity:0.4, marginBottom:14, textTransform:"uppercase" }}>Territories</p>
              {CONFIG.territories.slice(0,6).map(terr=>(
                <button key={terr.code} onClick={()=>handleTerritory(terr)}
                  style={{ display:"block", color:"rgba(255,255,255,0.4)", background:"none", border:"none", cursor:"pointer", fontSize:12, padding:"3px 0", fontFamily:B.f.body }}>
                  {terr.name}
                </button>
              ))}
            </div>
          </div>
          <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:20, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
            <span style={{ opacity:0.25, fontSize:11, fontFamily:B.f.body }}>© 2024 Vendifree. All rights reserved.</span>
            <span style={{ opacity:0.25, fontSize:11, fontFamily:B.f.mono }}>{territory.name} — {territory.currency} — {lang.toUpperCase()}</span>
          </div>
        </div>
      </footer>

      {/* ── MODALS ── */}
      {showPost  && <PostAd lang={lang} territory={territory} user={user} onClose={()=>setShowPost(false)}  onPost={handlePost} addToast={addToast}/>}
      {showLogin && <Login  lang={lang}                                   onClose={()=>setShowLogin(false)} onLogin={u=>{setUser(u);addToast(`Welcome, ${u.name}`);}}/>}

      {/* ── TOASTS ── */}
      <Toast items={toasts}/>

      {/* ── Overlay dismiss ── */}
      {(showTerr||showUser)&&<div style={{ position:"fixed", inset:0, zIndex:50 }} onClick={()=>{setShowTerr(false);setShowUser(false);}}/>}
    </div>
  );
}
